import { useState, useEffect, useRef } from "react";
import { api, setAccessToken, syncClock, serverTime } from "./api.js";
import {
  LayoutGrid,
  Users,
  ClipboardList,
  CalendarClock,
  CalendarDays,
  Wallet,
  Camera,
  Plus,
  Clock,
  Timer,
  Coffee,
  ImagePlus,
  Check,
  Trash2,
  BarChart3,
  Building2,
  Bell,
  Download,
  Megaphone,
  Settings,
  LogOut,
  ShieldCheck,
  Video,
  Radio,
  Menu,
  X,
  ThumbsUp,
} from "lucide-react";

const STEP_NAMES = ["Uttvätt", "Invändigt", "Polering", "Avslut", "Slutlig kontroll"];

const EXTRA_CATALOG = ["Uppfräschning", "Lackskydd (keramiskt)", "Motortvätt", "Läderrengöring", "Dubbdäcksbyte", "Ozonbehandling", "Vax", "Kemtvätt av sätena"];

// Hur lång tid kunden har på sig att kvittera att bilen är mottagen, räknat
// från att verkstaden markerat ordern klar.
// Måste stämma med jobbet i apps/api/src/jobs/kvittens.js — här används den
// bara för att visa hur lång tid kunden har kvar.
const KVITTENS_AUTO_MS = 48 * 60 * 60 * 1000;

const makeExtraStep = (name, staffId) => ({ name, done: false, at: null, timerRunning: false, accumulatedMs: 0, startedAt: null, staffIds: staffId ? [staffId] : [] });

// Måste stämma med MANAGER_ROLES i apps/api/src/middleware/auth.js. Här styr
// den bara vad som hämtas — servern avgör vad som faktiskt släpps igenom.
const MANAGER_ROLES_WEB = ["agare", "platschef", "arbetsledare"];

const ROLE_LABELS = {
  agare: "Admin",
  platschef: "Platschef",
  arbetsledare: "Arbetsledare",
  tekniker: "Personal",
  foretagskund: "Företagskund",
  plattform: "Plattform",
};

const STATUS = {
  vantar: { label: "Väntar", dot: "#FFC93C" },
  pagar: { label: "Pågår", dot: "#FF8A34" },
  klar: { label: "Klar", dot: "#1657C8" },
};

const BOOKING_STATUS = {
  ny: { label: "Ny bokning", dot: "#FFC93C" },
  mottagen: { label: "Mottagen", dot: "#5B9BD9" },
  pagar: { label: "Pågår", dot: "#FF8A34" },
  klar: { label: "Klar", dot: "#1657C8" },
};

const uid = () => Math.random().toString(36).slice(2, 9);
const today = () => new Date().toISOString().slice(0, 10);
const timeNow = () => new Date().toTimeString().slice(0, 5);
const toMinutes = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};
const addDays = (dateStr, n) => {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const mondayOf = (dateStr) => {
  const d = new Date(dateStr + "T00:00:00");
  const day = d.getDay();
  return addDays(dateStr, day === 0 ? -6 : 1 - day);
};
const dayLabel = (dateStr) => {
  const d = new Date(dateStr + "T00:00:00");
  const wd = ["Sön", "Mån", "Tis", "Ons", "Tor", "Fre", "Lör"][d.getDay()];
  return `${wd} ${d.getDate()}/${d.getMonth() + 1}`;
};

const makeSteps = (doneCount, staffId, labels = STEP_NAMES) =>
  labels.map((name, i) => ({
    name,
    done: i < doneCount,
    at: i < doneCount ? "08:1" + i : null,
    timerRunning: false,
    accumulatedMs: i < doneCount ? (18 + i * 7) * 60000 : 0,
    startedAt: null,
    staffIds: staffId ? [staffId] : [],
    isFinal: i === labels.length - 1,
  }));

// Serverns tid, inte datorns. Går den inloggades klocka fel skulle timern
// annars visa olika siffror för olika personer på samma moment.
const stepElapsedMs = (step) => step.accumulatedMs + (step.timerRunning && step.startedAt ? serverTime() - step.startedAt : 0);

/// Översätter ett moment från API:et till formen resten av appen använder.
///
/// Servern har inget timerRunning-fält: att startedAt är satt *betyder* att
/// timern går. Här delas det upp igen, eftersom gränssnittet är skrivet så.
const fromApiStep = (s) => ({
  id: s.id,
  name: s.name,
  done: s.done,
  at: s.doneAt ? new Date(s.doneAt).toTimeString().slice(0, 5) : null,
  timerRunning: Boolean(s.startedAt),
  accumulatedMs: s.accumulatedMs,
  startedAt: s.startedAt ? new Date(s.startedAt).getTime() : null,
  staffIds: s.staffIds ?? [],
  isFinal: s.isFinal,
});

const fromApiOrder = (o) => ({
  ...o,
  datum: typeof o.datum === "string" ? o.datum.slice(0, 10) : o.datum,
  steps: (o.steps ?? []).map(fromApiStep),
  extraSteps: (o.extraSteps ?? []).map(fromApiStep),
  chat: o.chat ?? [],
  photos: o.photos ?? [],
  klarAt: o.klarAt ? new Date(o.klarAt).getTime() : null,
  kvittens: o.kvittens
    ? { ...o.kvittens, at: new Date(o.kvittens.at).getTime() }
    : null,
  kvittensPaminnelseAt: o.kvittensPaminnelseAt
    ? new Date(o.kvittensPaminnelseAt).getTime()
    : null,
  hamtning: o.hamtning
    ? { ...o.hamtning, datum: o.hamtning.datum ? String(o.hamtning.datum).slice(0, 10) : "" }
    : null,
  aterlamning: o.aterlamning
    ? { ...o.aterlamning, datum: o.aterlamning.datum ? String(o.aterlamning.datum).slice(0, 10) : "" }
    : null,
});
const formatDuration = (ms) => {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60).toString().padStart(2, "0");
  const s = (totalSec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

const DEMO_COMPANY = "Bilcenter Kristianstad AB";

const PLAN_PRICES = { Basic: 499, Pro: 999 };
const TENANT_STATUS = {
  active: { label: "Aktiv", dot: "#1657C8" },
  trial: { label: "Provperiod", dot: "#FFC93C" },
  past_due: { label: "Betalning saknas", dot: "#FF8A34" },
  cancelled: { label: "Uppsagd", dot: "#94A3B8" },
};
const seedTenants = [
  { id: "t1", name: "Glansgruppen (Kristianstad)", plan: "Pro", status: "active", signupDate: "2026-01-15" },
  { id: "t2", name: "Bilcenter Detailing AB", plan: "Basic", status: "active", signupDate: "2026-03-02" },
  { id: "t3", name: "Nordic Car Spa", plan: "Pro", status: "trial", signupDate: "2026-08-20" },
  { id: "t4", name: "Skåne Bilvård & Lack", plan: "Basic", status: "past_due", signupDate: "2026-02-10" },
];

const seedOrders = [
  {
    id: "o1", kund: "Anders Lind", bil: "Volvo V70", tjanst: "Helglans + interiör",
    staffId: "s1", datum: today(), tid: "08:00", timmar: 3,
    steps: makeSteps(2, "s1"), extraSteps: [makeExtraStep("Lackskydd (keramiskt)", "s2")], notes: "Repor på höger dörr, kund vill ha extra polish där.", photos: [],
    company: null, bookingStatus: "mottagen", hamtning: null, aterlamning: null, chat: [],
  },
  {
    id: "o2", kund: "Sara Nyberg", bil: "Audi A3", tjanst: "Lackförsegling",
    staffId: "s2", datum: today(), tid: "10:00", timmar: 2,
    steps: makeSteps(0, "s2"), extraSteps: [], notes: "", photos: [],
    company: null, bookingStatus: "mottagen", hamtning: null, aterlamning: null, chat: [],
  },
  {
    id: "o3", kund: "Firma Bygg AB (5 bilar)", bil: "Transportbilar", tjanst: "Utvändig tvätt",
    staffId: "s3", datum: today(), tid: "07:00", timmar: 4,
    steps: makeSteps(5, "s3"), extraSteps: [], notes: "Klart innan lunch, kund nöjd.", photos: [],
    company: null, bookingStatus: "mottagen", hamtning: null, aterlamning: null, chat: [],
  },
  {
    id: "o4", kund: DEMO_COMPANY, bil: "Passat 2022, reg ABC123", tjanst: "Helglans inför visning",
    staffId: "", datum: addDays(today(), 1), tid: "09:00", timmar: 2,
    steps: makeSteps(0, ""),
    extraSteps: [makeExtraStep("Lackskydd (keramiskt)", "")],
    notes: "Kontakt: Jonas, 070-1234567", photos: [],
    company: DEMO_COMPANY, bookingStatus: "ny",
    hamtning: { mode: "datum", datum: addDays(today(), 1), tid: "09:00" },
    aterlamning: { mode: "normal", datum: "", tid: "" },
    chat: [{ id: uid(), senderName: "Jonas Petersson", senderOrg: DEMO_COMPANY, text: "Det finns en repa på vänster framskärm sedan tidigare, vill ha koll på den innan ni börjar.", image: null, at: "Igår 14:20", readByStaff: false, readByCustomer: true }],
  },
];

const fontImport = `
@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@500;600;700&family=Inter:wght@400;500;600&display=swap');
`;

const styles = {
  page: { minHeight: "100vh", background: "#E7EDF7", color: "#1F2A44", fontFamily: "'Inter', sans-serif", display: "flex" },
  sidebar: { width: 200, background: "#1657C8", borderRight: "none", padding: "24px 0", display: "flex", flexDirection: "column", flexShrink: 0 },
  brand: { fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 26, letterSpacing: 0.5, padding: "0 20px 4px", color: "#FFFFFF" },
  brandSub: { padding: "0 20px 20px", fontSize: 12, color: "rgba(255,255,255,0.75)", borderBottom: "1px solid rgba(255,255,255,0.2)", marginBottom: 12 },
  navItem: (active) => ({ display: "flex", alignItems: "center", gap: 10, padding: "10px 20px", fontSize: 14, color: active ? "#FFC93C" : "rgba(255,255,255,0.85)", background: active ? "rgba(255,255,255,0.12)" : "transparent", borderLeft: active ? "3px solid #FFC93C" : "3px solid transparent", cursor: "pointer" }),
  navBadge: { marginLeft: "auto", background: "#FF8A34", color: "#FFFFFF", fontSize: 10, fontWeight: 700, borderRadius: 10, padding: "1px 6px", minWidth: 16, textAlign: "center" },
  main: { flex: 1, padding: "28px 36px", overflowY: "auto" },
  h1: { fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 600, fontSize: 28, margin: "0 0 4px", color: "#1F2A44" },
  sub: { color: "#6B7789", fontSize: 13, margin: "0 0 24px" },
  cardRow: { display: "flex", gap: 14, marginBottom: 28, flexWrap: "wrap" },
  statCard: { background: "#FAFBFE", border: "1px solid #DEE6F3", padding: "16px 20px", minWidth: 140, boxShadow: "0 1px 3px rgba(16,24,40,0.05)" },
  statNum: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 30, fontWeight: 600, color: "#1657C8" },
  statLabel: { fontSize: 12, color: "#6B7789", marginTop: 2 },
  panel: { background: "#FAFBFE", border: "1px solid #DEE6F3", padding: "20px 22px", marginBottom: 20, boxShadow: "0 1px 3px rgba(16,24,40,0.05)" },
  panelTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 18, fontWeight: 600, margin: "0 0 14px", color: "#1F2A44" },
  row: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #E9EEF7", fontSize: 14, gap: 12 },
  input: { background: "#EEF2FA", border: "1px solid #D2DCEC", color: "#1F2A44", padding: "8px 10px", fontSize: 13, outline: "none" },
  label: { fontSize: 11, color: "#6B7789", marginBottom: 4, display: "block" },
  btn: { background: "#FF8A34", color: "#FFFFFF", border: "none", padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 },
  btnGhost: { background: "transparent", color: "#1657C8", border: "1px solid #C7D6F0", padding: "8px 14px", fontSize: 13, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 },
  btnSmall: { background: "transparent", color: "#94A3B8", border: "1px solid #D2DCEC", padding: "4px 8px", fontSize: 11, cursor: "pointer" },
  dot: (c) => ({ width: 8, height: 8, borderRadius: "50%", background: c, display: "inline-block", marginRight: 6 }),
  stepChip: (done) => ({
    display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, padding: "5px 10px",
    border: "1px solid " + (done ? "#9FC7FB" : "#D2DCEC"), background: done ? "#E4EEFD" : "#EEF2FA",
    color: done ? "#1657C8" : "#475467", cursor: "pointer",
  }),
  stepRow: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 10 },
  stepStatus: (state) => ({
    fontSize: 11, padding: "2px 8px", border: "1px solid",
    borderColor: state === "klar" ? "#9FC7FB" : state === "pagar" ? "#FFD98A" : "#D2DCEC",
    color: state === "klar" ? "#1657C8" : state === "pagar" ? "#B5720A" : "#6B7789",
    background: state === "klar" ? "#E4EEFD" : state === "pagar" ? "#FFF3D6" : "transparent",
  }),
  timerBox: { display: "flex", alignItems: "center", gap: 8, flexShrink: 0 },
  timerText: { fontSize: 12, color: "#6B7789", minWidth: 42, textAlign: "right", fontVariantNumeric: "tabular-nums" },
  actionBtn: { background: "transparent", border: "1px solid #D2DCEC", color: "#475467", padding: "4px 10px", fontSize: 12, cursor: "pointer" },
  actionBtnDone: { background: "#1657C8", border: "none", color: "#FFFFFF", padding: "4px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer" },
  timeEditInput: { background: "#EEF2FA", border: "1px solid #D2DCEC", color: "#1F2A44", padding: "3px 6px", fontSize: 12, width: 50, textAlign: "right" },
  extraLabel: { fontSize: 11, color: "#6B7789", margin: "10px 0 6px" },
  addExtraBtn: { background: "transparent", border: "1px dashed #C7D6F0", color: "#1657C8", padding: "4px 10px", fontSize: 11, cursor: "pointer", marginRight: 6, marginBottom: 6 },
  stepStaffSelect: { background: "#EEF2FA", border: "1px solid #D2DCEC", color: "#1F2A44", padding: "3px 6px", fontSize: 11, minWidth: 110 },
  stepStaffText: { fontSize: 11, color: "#6B7789", minWidth: 90 },
  staffToggle: (active) => ({
    fontSize: 10, padding: "3px 7px", border: "1px solid " + (active ? "#1657C8" : "#D2DCEC"),
    background: active ? "#E4EEFD" : "transparent", color: active ? "#1657C8" : "#6B7789", cursor: "pointer",
  }),
  reportCard: { background: "#FAFBFE", border: "1px solid #DEE6F3", padding: "16px 18px", marginBottom: 14, boxShadow: "0 1px 3px rgba(16,24,40,0.05)" },
  stationChip: { fontSize: 11, padding: "3px 8px", border: "1px solid #D2DCEC", background: "#EEF2FA", color: "#475467", display: "inline-flex", alignItems: "center", gap: 6 },
  stationChipX: { background: "transparent", border: "none", color: "#94A3B8", cursor: "pointer", padding: 0, fontSize: 13, lineHeight: 1 },
  reportName: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 18, fontWeight: 600, color: "#1F2A44" },
  reportItem: { fontSize: 12, color: "#6B7789", padding: "3px 0", borderTop: "1px solid #E9EEF7" },
};

function StatusPill({ status }) {
  const s = STATUS[status];
  return (
    <span style={{ display: "inline-flex", alignItems: "center", fontSize: 12, color: "#475467" }}>
      <span style={styles.dot(s.dot)} />
      {s.label}
    </span>
  );
}

function BookingStatusPill({ status }) {
  const s = BOOKING_STATUS[status] || BOOKING_STATUS.ny;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", fontSize: 12, color: "#475467" }}>
      <span style={styles.dot(s.dot)} />
      {s.label}
    </span>
  );
}

function getOrderStatus(order) {
  const done = order.steps.filter((s) => s.done).length;
  if (done === 0) return "vantar";
  if (done === order.steps.length) return "klar";
  return "pagar";
}

export default function GlansgruppenApp() {
  const [authStage, setAuthStage] = useState("login");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({ company: "", contact: "", email: "", password: "" });
  const [authError, setAuthError] = useState("");

  const [tab, setTab] = useState("oversikt");
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 860 : false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 860);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  // Den inloggade användaren kommer från servern och går inte att ändra i
  // webbläsaren. Rollen låg tidigare i en rullgardinsmeny — den fanns bara för
  // att kunna demonstrera vyerna innan det fanns en backend.
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const role = currentUser?.role ?? "tekniker";
  const canSeeLoner = role === "agare";
  const canManageStaff = role === "platschef" || role === "agare";
  const isTekniker = role === "tekniker";
  const isForetag = role === "foretagskund";
  const isPlatform = role === "plattform";
  const canManageWork = !isTekniker && !isForetag && !isPlatform;

  const PORTAL_TABS = ["mina-bokningar", "ny-bokning", "kundmeddelanden", "kundmoten", "mitt-konto"];
  const PLATFORM_TABS = ["plattform", "mitt-konto"];

  useEffect(() => {
    if (tab === "loner" && !canSeeLoner) setTab("oversikt");
    if (tab === "rapport" && !canManageStaff) setTab("oversikt");
    if (tab === "installningar" && !canManageStaff) setTab("oversikt");
    if (tab === "statistik" && !canManageStaff) setTab("oversikt");
    if (tab === "kundkontakt" && !canManageStaff) setTab("oversikt");
    if (tab === "konton" && role !== "agare") setTab("oversikt");
    if (isForetag && !PORTAL_TABS.includes(tab)) setTab("mina-bokningar");
    if (!isForetag && !isPlatform && PORTAL_TABS.includes(tab)) setTab("oversikt");
    if (isPlatform && !PLATFORM_TABS.includes(tab)) setTab("plattform");
    if (!isPlatform && PLATFORM_TABS.includes(tab)) setTab("oversikt");
  }, [role]);

  // Företagsnamnet kommer från kontot och går inte längre att skriva över i
  // gränssnittet — annars kunde vem som helst byta till en annan kunds namn.
  const companyName = currentUser?.companyName ?? "";
  // Kontaktpersonen är helt enkelt den inloggade.
  const loggedInContact = currentUser?.role === "foretagskund" ? currentUser : null;

  const [soundOn, setSoundOn] = useState(true);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [inviteForm, setInviteForm] = useState({ name: "", email: "", role: "tekniker" });
  // Länken visas en enda gång, direkt efter att inbjudan skapats. Servern
  // lagrar bara ett hash av token och kan aldrig visa den igen.
  const [senasteLank, setSenasteLank] = useState(null);
  const [authBusy, setAuthBusy] = useState(false);

  /// Tar emot svaret från /auth/login eller /auth/me och ställer in sessionen.
  const applySession = (data) => {
    syncClock(data.serverNow);
    setCurrentUser(data.user);
    setAuthStage("in");
    setAuthError("");
    if (data.user.role === "foretagskund") {
      setBookingForm((prev) => ({ ...prev, kontakt: prev.kontakt || data.user.name }));
    }
    if (data.settings) {
      // Inställningarna kommer från servern här — de ska inte skrivas tillbaka
      // direkt som om användaren ändrat något.
      hoppaOverNastaSparning.current = true;
      // Verkstadens egna momentnamn, tillval och priser.
      setShopName(data.settings.shopName);
      setShopSubtitle(data.settings.shopSubtitle ?? "");
      if (data.settings.stepLabels?.length) setStepLabels(data.settings.stepLabels);
      if (data.settings.extraCatalog?.length) setExtraCatalog(data.settings.extraCatalog);
      if (data.settings.prices) setPrices(data.settings.prices);
    }
    laddaData(data.user.role);
  };

  // --- Glömt lösenord ---
  const [glomtEpost, setGlomtEpost] = useState("");
  const [glomtSvar, setGlomtSvar] = useState("");

  const begarAterstallning = async () => {
    if (!glomtEpost.trim()) {
      setAuthError("Ange din e-postadress.");
      return;
    }
    setAuthBusy(true);
    try {
      const svar = await api.forgotPassword(glomtEpost.trim());
      setGlomtSvar(svar.message);
      setAuthError("");
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthBusy(false);
    }
  };

  // --- Återställningslänk ---
  const [aterstall, setAterstall] = useState(null);
  const [aterstallForm, setAterstallForm] = useState({ losen: "", upprepa: "" });
  const [aterstallKlar, setAterstallKlar] = useState(null);

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("aterstall");
    if (!token) return;
    api.lookupReset(token)
      .then((s) => setAterstall({ token, ...s }))
      .catch((err) => setAterstall({ token, fel: err.message }));
  }, []);

  const sattNyttLosenord = async () => {
    if (aterstallForm.losen !== aterstallForm.upprepa) {
      setAuthError("Lösenorden matchar inte.");
      return;
    }
    if (aterstallForm.losen.length < 8) {
      setAuthError("Lösenordet måste vara minst 8 tecken.");
      return;
    }
    setAuthBusy(true);
    try {
      const svar = await api.resetPassword(aterstall.token, aterstallForm.losen);
      setAterstallKlar(svar.email);
      setAuthError("");
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthBusy(false);
    }
  };

  // --- Inbjudningslänk ---
  //
  // Öppnar någon appen med ?inbjudan=TOKEN ska hen mötas av lösenordsvalet,
  // inte av inloggningsrutan hen ändå inte kan använda än.
  const [inbjudan, setInbjudan] = useState(null);
  const [inbjudanForm, setInbjudanForm] = useState({ losen: "", upprepa: "" });
  const [inbjudanKlar, setInbjudanKlar] = useState(null);

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("inbjudan");
    if (!token) return;
    api.lookupInvite(token)
      .then((s) => setInbjudan({ token, ...s.invite }))
      .catch((err) => setInbjudan({ token, fel: err.message }));
  }, []);

  const acceptteraInbjudan = async () => {
    if (inbjudanForm.losen !== inbjudanForm.upprepa) {
      setAuthError("Lösenorden matchar inte.");
      return;
    }
    if (inbjudanForm.losen.length < 8) {
      setAuthError("Lösenordet måste vara minst 8 tecken.");
      return;
    }
    setAuthBusy(true);
    try {
      const svar = await api.acceptInvite(inbjudan.token, inbjudanForm.losen);
      setInbjudanKlar(svar.email);
      setAuthError("");
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthBusy(false);
    }
  };

  // Återuppta sessionen vid sidladdning. Refresh-cookien ligger kvar i
  // webbläsaren, så den som var inloggad slipper logga in igen.
  useEffect(() => {
    let avbruten = false;
    api.restoreSession()
      .then((data) => { if (!avbruten && data) applySession(data); })
      .catch(() => {}) // Ingen giltig session är det normala, inte ett fel.
      .finally(() => { if (!avbruten) setAuthLoading(false); });
    return () => { avbruten = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async () => {
    if (!loginForm.email.trim() || !loginForm.password.trim()) {
      setAuthError("Ange e-post och lösenord.");
      return;
    }
    setAuthBusy(true);
    try {
      const data = await api.login(loginForm.email.trim(), loginForm.password);
      setAccessToken(data.accessToken);
      applySession(data);
      setLoginForm({ email: "", password: "" });
    } catch (err) {
      // Serverns text är redan formulerad för användaren, på svenska.
      setAuthError(err.message);
    } finally {
      setAuthBusy(false);
    }
  };

  const handleRegister = async () => {
    if (!registerForm.company.trim() || !registerForm.email.trim() || !registerForm.password.trim()) {
      setAuthError("Fyll i företagsnamn, e-post och lösenord.");
      return;
    }
    setAuthBusy(true);
    try {
      await api.register({
        company: registerForm.company.trim(),
        contact: registerForm.contact.trim(),
        email: registerForm.email.trim(),
        password: registerForm.password,
      });
      setAuthError("");
      setAuthStage("pending-approval");
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthBusy(false);
    }
  };

  // --- Agera som ---
  //
  // Ägaren går in i någon annans konto helt och hållet. Servern avgör vem som
  // får göra det utifrån den verkliga aktören i token, aldrig utifrån kontot
  // man för tillfället sitter i.
  const [agerarSom, setAgerarSom] = useState(null);
  const [mojligaKonton, setMojligaKonton] = useState([]);
  // Man kan bara befinna sig i ett agerar-läge om man är ägare, så båda
  // fallen betyder att växlaren ska visas.
  const arAgare = currentUser?.role === "agare" || agerarSom !== null;

  useEffect(() => {
    if (!arAgare) { setMojligaKonton([]); return; }
    api.viewAsTargets()
      .then((s) => setMojligaKonton(s.targets))
      .catch(() => setMojligaKonton([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arAgare, currentUser?.id]);

  const agerSom = async (userId) => {
    try {
      const svar = await api.viewAs(userId || null);
      setAccessToken(svar.accessToken);
      setCurrentUser(svar.user);
      setAgerarSom(svar.agerarSom);
      setTab(
        svar.user.role === "foretagskund" ? "mina-bokningar"
        : svar.user.role === "plattform" ? "plattform"
        : "oversikt"
      );
      // Datan måste hämtas om — personerna ser olika saker.
      await laddaData(svar.user.role);
    } catch (err) {
      setDataError(err.message);
    }
  };

  // --- Lösenordsbyte ---
  const [pwForm, setPwForm] = useState({ current: "", ny: "", upprepa: "" });
  const [pwMelding, setPwMelding] = useState({ typ: "", text: "" });
  const [pwBusy, setPwBusy] = useState(false);

  const bytLosenord = async () => {
    setPwMelding({ typ: "", text: "" });
    if (pwForm.ny !== pwForm.upprepa) {
      setPwMelding({ typ: "fel", text: "De nya lösenorden matchar inte." });
      return;
    }
    if (pwForm.ny.length < 8) {
      setPwMelding({ typ: "fel", text: "Det nya lösenordet måste vara minst 8 tecken." });
      return;
    }
    setPwBusy(true);
    try {
      const svar = await api.changePassword(pwForm.current, pwForm.ny);
      setPwForm({ current: "", ny: "", upprepa: "" });
      setPwMelding({ typ: "ok", text: svar.message });
      // Tvånget är hävt — hämta om kontot och släpp in användaren.
      const me = await api.me();
      applySession(me);
    } catch (err) {
      setPwMelding({ typ: "fel", text: err.message });
    } finally {
      setPwBusy(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.logout();
    } finally {
      setAccessToken(null);
      setCurrentUser(null);
      setAuthStage("login");
    }
  };
  /// Skapar en inbjudan och visar länken. Inget mejl skickas — du förmedlar
  /// länken själv, och personen väljer sitt eget lösenord.
  const skapaInbjudan = async (data) => {
    try {
      const svar = await api.createInvite(data);
      setSenasteLank({
        lank: svar.lank ?? `${window.location.origin}/?inbjudan=${svar.token}`,
        namn: svar.invite.name,
        epost: svar.invite.email,
        mejl: svar.mejl,
      });
      setPendingInvites((await api.invites()).invites);
      setDataError("");
      return true;
    } catch (err) {
      setDataError(err.message);
      return false;
    }
  };

  const sendInvite = async () => {
    if (!inviteForm.name.trim() || !inviteForm.email.trim()) return;
    const ok = await skapaInbjudan({
      kind: "staff",
      name: inviteForm.name.trim(),
      email: inviteForm.email.trim(),
      role: inviteForm.role,
    });
    if (ok) setInviteForm({ name: "", email: "", role: "tekniker" });
  };

  const cancelInvite = async (id) => {
    try {
      await api.cancelInvite(id);
      setPendingInvites((prev) => prev.filter((i) => i.id !== id));
    } catch (err) {
      setDataError(err.message);
    }
  };

  // Personal- och kundinbjudningar ligger i samma tabell och skiljs på kind,
  // så listorna härleds i stället för att hållas var för sig.
  const personalInbjudningar = pendingInvites.filter((i) => i.kind === "staff");
  const pendingCustomerInvites = pendingInvites.filter((i) => i.kind === "customer");
  const [customerInviteForm, setCustomerInviteForm] = useState({ company: "", contact: "", email: "" });
  const sendCustomerInvite = async () => {
    if (!customerInviteForm.company.trim() || !customerInviteForm.email.trim()) return;
    const ok = await skapaInbjudan({
      kind: "customer",
      // Saknas kontaktperson används företagsnamnet, så att kontot får ett namn.
      name: customerInviteForm.contact.trim() || customerInviteForm.company.trim(),
      email: customerInviteForm.email.trim(),
      company: customerInviteForm.company.trim(),
    });
    if (ok) setCustomerInviteForm({ company: "", contact: "", email: "" });
  };
  const cancelCustomerInvite = cancelInvite;
  const [announcements, setAnnouncements] = useState([]);
  const [customerMessages, setCustomerMessages] = useState([]);
  const [newCustomerMessage, setNewCustomerMessage] = useState({ title: "", text: "", target: "alla" });
  const [meetings, setMeetings] = useState([]);
  const [newMeeting, setNewMeeting] = useState({ title: "", type: "Uppföljning", target: "alla", datum: today(), tid: "10:00" });
  const [newAnnouncement, setNewAnnouncement] = useState({ title: "", text: "" });
  const [bookingForm, setBookingForm] = useState({
    bil: "", info: "", kontakt: "", services: [],
    hamtningMode: "normal", hamtningDatum: "", hamtningTid: "",
    aterlamningMode: "normal", aterlamningDatum: "", aterlamningTid: "",
  });
  const [chatDraft, setChatDraft] = useState({});
  const toggleBookingService = (name) => {
    setBookingForm((prev) => ({
      ...prev,
      services: prev.services.includes(name) ? prev.services.filter((s) => s !== name) : [...prev.services, name],
    }));
  };

  // Kunden ska kunna be om vad som helst, inte bara det som råkar finnas i
  // katalogen. Egna tjänster läggs till i samma services-lista som de
  // förbockade, så resten av flödet behöver inte veta skillnaden.
  const [customServiceDraft, setCustomServiceDraft] = useState("");
  const addCustomService = () => {
    const name = customServiceDraft.trim();
    if (!name) return;
    // Undvik dubbletter oavsett skiftläge, annars kan "Vax" och "vax" hamna
    // som två separata moment på samma order.
    const exists = bookingForm.services.some((s) => s.toLowerCase() === name.toLowerCase());
    if (!exists) toggleBookingService(name);
    setCustomServiceDraft("");
  };

  // Ordrar och personal kommer från databasen. Tomma listor tills första
  // hämtningen är klar — inga påhittade rader som ser äkta ut.
  const [staff, setStaff] = useState([]);
  const [orders, setOrders] = useState([]);
  const [dataError, setDataError] = useState("");

  /// Ersätter en order i listan med serverns version.
  ///
  /// Varje anrop som ändrar en order svarar med hela ordern, så vi slipper
  /// gissa hur resultatet blev. Skulle servern räkna annorlunda än vi trodde
  /// är det serverns svar som gäller.
  const replaceOrder = (o) => {
    const normaliserad = fromApiOrder(o);
    setOrders((prev) => prev.map((x) => (x.id === normaliserad.id ? normaliserad : x)));
  };

  const laddaData = async (roll) => {
    try {
      const svar = await api.orders();
      syncClock(svar.serverNow);
      const hamtade = svar.orders.map(fromApiOrder);
      setOrders(hamtade);
      // Formulärens förval kan inte sättas förrän det finns något att välja.
      setDocOrderId((prev) => prev || hamtade[0]?.id || "");

      // Kundportalen och verkstaden ser olika delar av systemet, så vi hämtar
      // bara det som rollen faktiskt får se — annars blir det en rad 403:or.
      const [meddelanden, moten] = await Promise.all([
        api.customerMessages(),
        api.meetings(),
      ]);
      setCustomerMessages(meddelanden.messages);
      setMeetings(moten.meetings);

      if (roll !== "foretagskund") {
        const [s, sch, fr, ny] = await Promise.all([
          api.staff(),
          api.schedule(),
          api.absences(),
          api.announcements(),
        ]);
        setStaff(s.staff);
        const forstaId = s.staff[0]?.id ?? "";
        setNewOrder((prev) => ({ ...prev, staffId: prev.staffId || forstaId }));
        setNewAbsence((prev) => ({ ...prev, staffId: prev.staffId || forstaId }));
        setScheduleEntries(sch.entries);
        setPublications(sch.publications);
        setAbsences(fr.absences);
        setAnnouncements(ny.announcements);

        if (MANAGER_ROLES_WEB.includes(roll)) {
          const [k, inb] = await Promise.all([api.customers(), api.invites()]);
          setRegisteredCustomers(k.customers);
          setPendingInvites(inb.invites);
        }
      }
      setDataError("");
    } catch (err) {
      setDataError(err.message);
    }
  };
  const [timeLogs, setTimeLogs] = useState([]);
  // Sätts när ordrarna hämtats — det finns inget att välja innan dess.
  const [docOrderId, setDocOrderId] = useState("");
  const [newStaff, setNewStaff] = useState({ name: "", roll: "", rate: "" });
  const [newOrder, setNewOrder] = useState({ kund: "", bil: "", tjanst: "", staffId: "", datum: today(), tid: "09:00", timmar: 1 });
  const [customExtra, setCustomExtra] = useState({});
  const [reportFrom, setReportFrom] = useState(today());
  const [reportTo, setReportTo] = useState(today());
  const [reportView, setReportView] = useState("personal");
  const [statCustomerView, setStatCustomerView] = useState(null);
  useEffect(() => {
    if (tab !== "statistik" && statCustomerView) setStatCustomerView(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  const [prices, setPrices] = useState({ "Uttvätt": 5, "Invändigt": 10, "Polering": 15, "Avslut": 5, "Slutlig kontroll": 5 });
  const getPrice = (name) => prices[name] ?? 0;
  const [shopName, setShopName] = useState("Glansgruppen");
  const [shopSubtitle, setShopSubtitle] = useState("Kristianstad · Bilvård");
  const [stepLabels, setStepLabels] = useState(["Uttvätt", "Invändigt", "Polering", "Avslut", "Slutlig kontroll"]);
  const [extraCatalog, setExtraCatalog] = useState(EXTRA_CATALOG);
  const [newExtraCatalogItem, setNewExtraCatalogItem] = useState("");
  const renameStepLabel = (i, newName) => {
    const oldName = stepLabels[i];
    setStepLabels((prev) => prev.map((n, idx) => (idx === i ? newName : n)));
    if (oldName !== newName) {
      setPrices((prev) => {
        const { [oldName]: oldPrice, ...rest } = prev;
        return { ...rest, [newName]: oldPrice ?? 0 };
      });
    }
  };
  const addExtraCatalogItem = () => {
    const name = newExtraCatalogItem.trim();
    if (!name || extraCatalog.includes(name)) return;
    setExtraCatalog((prev) => [...prev, name]);
    setNewExtraCatalogItem("");
  };
  const removeExtraCatalogItem = (name) => setExtraCatalog((prev) => prev.filter((n) => n !== name));

  // Inställningarna sparas en kort stund efter sista ändringen, i stället för
  // vid varje tangenttryckning — annars blir det ett API-anrop per bokstav när
  // någon döper om ett moment.
  const hoppaOverNastaSparning = useRef(true);
  useEffect(() => {
    if (hoppaOverNastaSparning.current) {
      hoppaOverNastaSparning.current = false;
      return;
    }
    if (!currentUser || !MANAGER_ROLES_WEB.includes(currentUser.role)) return;

    const id = setTimeout(() => {
      api
        .updateSettings({ shopName, shopSubtitle, stepLabels, extraCatalog, prices })
        .catch((err) => setDataError(err.message));
    }, 800);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopName, shopSubtitle, stepLabels, extraCatalog, prices]);

  /// Tjänster kunden skrivit in själv, alltså de som inte finns i någon katalog.
  const customServices = bookingForm.services.filter(
    (s) => !stepLabels.includes(s) && !extraCatalog.includes(s)
  );

  const rekondPackage = [stepLabels[0], stepLabels[1], "Motortvätt", stepLabels[2]].filter(Boolean);
  const isRekondSelected = rekondPackage.every((n) => bookingForm.services.includes(n));
  const toggleRekondPackage = () => {
    setBookingForm((prev) => {
      const allIn = rekondPackage.every((n) => prev.services.includes(n));
      return {
        ...prev,
        services: allIn
          ? prev.services.filter((s) => !rekondPackage.includes(s))
          : Array.from(new Set([...prev.services, ...rekondPackage])),
      };
    });
  };

  const [weekAnchor, setWeekAnchor] = useState(today());

  // Servern lagrar schemat som en platt lista med poster. Gränssnittet slår upp
  // per dag och person, så uppdelningen görs här i stället för att hålla två
  // format i minnet samtidigt.
  const [scheduleEntries, setScheduleEntries] = useState([]);
  const [publications, setPublications] = useState([]);

  const weekDates = [0, 1, 2, 3, 4, 5, 6].map((i) => addDays(mondayOf(weekAnchor), i));
  const [assignForm, setAssignForm] = useState({ staffId: "", date: today(), kind: "station", station: "", freeText: "", start: "07:00", end: "12:00" });

  // Förvalen kan inte sättas vid start — personal och moment finns inte förrän
  // de hämtats från servern.
  useEffect(() => {
    setAssignForm((prev) => ({
      ...prev,
      staffId: prev.staffId || staff[0]?.id || "",
      station: prev.station || stepLabels[0] || "",
      date: weekDates.includes(prev.date) ? prev.date : weekDates[0],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staff, stepLabels, weekAnchor]);

  const getAssignments = (date, staffId) =>
    scheduleEntries.filter((e) => e.date === date && e.staffId === staffId);

  const veckansPublikation = publications.find((p) => p.weekStart === weekDates[0]);
  const weekPublished = Boolean(veckansPublikation);
  const weekPublishedAt = veckansPublikation
    ? new Date(veckansPublikation.publishedAt).toLocaleString("sv-SE")
    : null;

  const removeEntryForDay = async (date, staffId, id) => {
    try {
      await api.removeScheduleEntry(id);
      setScheduleEntries((prev) => prev.filter((e) => e.id !== id));
    } catch (err) {
      setDataError(err.message);
    }
  };

  const submitAssignment = async () => {
    if (assignForm.kind === "fritext" && !assignForm.freeText.trim()) return;
    if (!assignForm.staffId) return;
    try {
      const svar = await api.addScheduleEntry({
        staffId: assignForm.staffId,
        date: assignForm.date,
        kind: assignForm.kind,
        station: assignForm.kind === "station"
          ? assignForm.station
          : assignForm.kind === "fritext"
            ? assignForm.freeText.trim()
            : "",
        start: assignForm.start,
        end: assignForm.end,
      });
      setScheduleEntries((prev) => [...prev, svar.entry]);
    } catch (err) {
      setDataError(err.message);
    }
  };

  const publishWeek = async () => {
    try {
      const svar = await api.publishWeek(weekDates[0]);
      setPublications((prev) => [
        ...prev.filter((p) => p.weekStart !== weekDates[0]),
        svar.publication,
      ]);
      // Servern lägger samtidigt upp en nyhet till personalen.
      setAnnouncements((await api.announcements()).announcements);
    } catch (err) {
      setDataError(err.message);
    }
  };

  const unpublishWeek = async () => {
    try {
      await api.unpublishWeek(weekDates[0]);
      setPublications((prev) => prev.filter((p) => p.weekStart !== weekDates[0]));
    } catch (err) {
      setDataError(err.message);
    }
  };

  const staffName = (id) => staff.find((s) => s.id === id)?.name || "Otilldelad";


  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Paminnelse efter 24 h och automatisk kvittens efter 48 h skots numera av
  // ett schemalagt jobb i API:et (apps/api/src/jobs/kvittens.js). Det lag
  // tidigare har, men tickade bara medan nagon hade appen oppen.

  /// Slår upp id:t för ett moment. Gränssnittet arbetar med positioner i
  /// listan, medan API:et vill ha momentets id.
  const stepIdAt = (orderId, field, idx) =>
    orders.find((o) => o.id === orderId)?.[field]?.[idx]?.id ?? null;

  /// Kör ett timer-anrop och ersätter momentet med serverns version.
  ///
  /// Ingen optimistisk uppdatering här: det är servern som räknar tiden, och
  /// att gissa resultatet i förväg skulle bara riskera att visa en siffra som
  /// sedan hoppar till en annan.
  const stepAction = async (orderId, field, idx, anrop) => {
    const stepId = stepIdAt(orderId, field, idx);
    if (!stepId) return;
    try {
      const svar = await anrop(orderId, stepId);
      syncClock(svar.serverNow);
      const uppdaterat = fromApiStep(svar.step);
      setOrders((prev) => prev.map((o) => (
        o.id === orderId
          ? { ...o, [field]: o[field].map((s, i) => (i === idx ? uppdaterat : s)) }
          : o
      )));
    } catch (err) {
      setDataError(err.message);
    }
  };

  const startStep = (orderId, field, idx) =>
    stepAction(orderId, field, idx, api.startStep);
  const completeStep = (orderId, field, idx) =>
    stepAction(orderId, field, idx, api.completeStep);
  const pauseStep = (orderId, field, idx) =>
    stepAction(orderId, field, idx, api.pauseStep);
  const setStepMinutes = (orderId, field, idx, minutes) =>
    stepAction(orderId, field, idx, (o, s) =>
      api.setStepMinutes(o, s, Math.max(0, minutes))
    );

  /// Bocka av eller ångra ett grundmoment.
  const toggleStep = (orderId, idx) => {
    const step = orders.find((o) => o.id === orderId)?.steps?.[idx];
    if (!step) return;
    // Servern har ingen "ångra klar" än, så en avbockning pausar i stället —
    // tiden är kvar och momentet kan bockas av igen.
    if (step.done) return;
    return completeStep(orderId, "steps", idx);
  };
  const toggleStepStaff = (orderId, field, idx, staffId) => {
    setOrders((prev) => prev.map((o) => (o.id === orderId ? {
      ...o,
      [field]: o[field].map((s, i) => {
        if (i !== idx) return s;
        const has = s.staffIds.includes(staffId);
        return { ...s, staffIds: has ? s.staffIds.filter((id) => id !== staffId) : [...s.staffIds, staffId] };
      }),
    } : o)));
  };
  const activateExtra = (orderId, name) => {
    if (!name.trim()) return;
    setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, extraSteps: [...o.extraSteps, makeExtraStep(name.trim(), o.staffId)] } : o)));
  };
  const removeExtra = (orderId, idx) => {
    setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, extraSteps: o.extraSteps.filter((_, i) => i !== idx) } : o)));
  };

  const addStaff = async () => {
    if (!newStaff.name.trim() || !newStaff.rate) return;
    try {
      const svar = await api.createStaff({
        name: newStaff.name.trim(),
        roll: newStaff.roll.trim() || "Detailer",
        rate: Number(newStaff.rate),
      });
      setStaff((prev) => [...prev, svar.staff]);
      setNewStaff({ name: "", roll: "", rate: "" });
    } catch (err) {
      setDataError(err.message);
    }
  };
  const setStaffPermissionRole = (staffId, permissionRole) => setStaff((prev) => prev.map((s) => (s.id === staffId ? { ...s, permissionRole } : s)));
  const toggleStaffStatus = (staffId) => setStaff((prev) => prev.map((s) => (s.id === staffId ? { ...s, status: s.status === "active" ? "inactive" : "active" } : s)));
  const removeStaffAccount = (staffId) => setStaff((prev) => prev.filter((s) => s.id !== staffId));

  /// Ägaren sätter ett tillfälligt lösenord åt någon som glömt sitt.
  ///
  /// Lösenordet visas en enda gång, här och nu, och ska förmedlas muntligt.
  /// Det finns ingen väg att se det igen — servern lagrar bara ett hash.
  const aterstallLosenord = async (staffId, namn) => {
    const nytt = window.prompt(
      `Sätt ett tillfälligt lösenord för ${namn}.\n\n` +
      `Personen tvingas välja ett eget vid nästa inloggning, och alla ` +
      `nuvarande inloggningar loggas ut.\n\nMinst 8 tecken:`
    );
    if (nytt === null) return;
    if (nytt.trim().length < 8) {
      setDataError("Lösenordet måste vara minst 8 tecken.");
      return;
    }
    try {
      const svar = await api.resetStaffPassword(staffId, nytt.trim());
      setDataError("");
      window.alert(
        `${svar.message}\n\nTillfälligt lösenord: ${nytt.trim()}\n\n` +
        `Skriv ner det nu — det går inte att se igen.`
      );
    } catch (err) {
      setDataError(err.message);
    }
  };

  const [tenants, setTenants] = useState(seedTenants);
  const [newTenant, setNewTenant] = useState({ name: "", plan: "Basic", status: "trial" });
  const addTenant = () => {
    if (!newTenant.name.trim()) return;
    setTenants((prev) => [...prev, { id: uid(), name: newTenant.name.trim(), plan: newTenant.plan, status: newTenant.status, signupDate: today() }]);
    setNewTenant({ name: "", plan: "Basic", status: "trial" });
  };
  const setTenantPlan = (id, plan) => setTenants((prev) => prev.map((t) => (t.id === id ? { ...t, plan } : t)));
  const setTenantStatus = (id, status) => setTenants((prev) => prev.map((t) => (t.id === id ? { ...t, status } : t)));
  const removeTenant = (id) => setTenants((prev) => prev.filter((t) => t.id !== id));

  const [registeredCustomers, setRegisteredCustomers] = useState([
    {
      id: "c1", company: DEMO_COMPANY, contact: "Jonas Petersson", email: "jonas@bilcenterkristianstad.se", status: "active",
      contacts: [
        { id: "ct1", name: "Jonas Petersson", email: "jonas@bilcenterkristianstad.se" },
        { id: "ct2", name: "Sara Lindqvist", email: "sara@bilcenterkristianstad.se" },
      ],
    },
  ]);
  /// Ersätter ett kundföretag i listan med serverns version.
  const replaceCustomer = (c) =>
    setRegisteredCustomers((prev) => prev.map((x) => (x.id === c.id ? c : x)));

  const setCustomerStatus = async (id, status) => {
    try {
      const svar = await api.setCustomerStatus(id, status);
      replaceCustomer(svar.customer);
    } catch (err) {
      setDataError(err.message);
    }
  };

  const toggleCustomerStatus = (id) => {
    const c = registeredCustomers.find((x) => x.id === id);
    if (!c) return;
    return setCustomerStatus(id, c.status === "active" ? "inactive" : "active");
  };
  const approveCustomerAccount = (id) => setCustomerStatus(id, "active");

  const removeCustomerAccount = async (id) => {
    try {
      await api.removeCustomer(id);
      setRegisteredCustomers((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      setDataError(err.message);
    }
  };
  const rejectCustomerAccount = removeCustomerAccount;

  const myCompanyAccount = registeredCustomers.find((c) => c.company === companyName);
  const [adminContactForm, setAdminContactForm] = useState({});
  const getAdminContactForm = (companyId) => adminContactForm[companyId] || { name: "", email: "" };
  const adminAddContact = async (companyId) => {
    const form = getAdminContactForm(companyId);
    if (!form.name.trim()) return;
    try {
      const svar = await api.addContact(companyId, {
        name: form.name.trim(),
        email: form.email.trim(),
        // Skickas bara om det faktiskt fyllts i — annars skapas inget konto.
        ...(form.password ? { password: form.password } : {}),
      });
      replaceCustomer(svar.customer);
      setAdminContactForm((prev) => ({ ...prev, [companyId]: { name: "", email: "", password: "" } }));
    } catch (err) {
      setDataError(err.message);
    }
  };
  /// Bjuder in ytterligare en kontaktperson till ett befintligt företag.
  ///
  /// Företaget tas från raden man står på i stället för att skrivas in, så
  /// att en felstavning inte tyst lägger upp ett nytt företag med nästan
  /// samma namn.
  const bjudInKontakt = async (company) => {
    const form = getAdminContactForm(company.id);
    if (!form.name.trim() || !form.email.trim()) {
      setDataError("Både namn och e-post behövs för en inbjudan.");
      return;
    }
    const ok = await skapaInbjudan({
      kind: "customer",
      name: form.name.trim(),
      email: form.email.trim(),
      customerCompanyId: company.id,
    });
    if (ok) {
      setAdminContactForm((prev) => ({ ...prev, [company.id]: { name: "", email: "", password: "" } }));
    }
  };

  const adminRemoveContact = async (companyId, contactId) => {
    try {
      const svar = await api.removeContact(companyId, contactId);
      replaceCustomer(svar.customer);
    } catch (err) {
      setDataError(err.message);
    }
  };

  const setCovering = (staffId, covering) => setStaff((prev) => prev.map((s) => (s.id === staffId ? { ...s, covering } : s)));
  const clearCovering = (staffId) => setCovering(staffId, null);
  const [coveringForm, setCoveringForm] = useState({});

  const ABSENCE_TYPES = ["Semester", "Sjuk", "Tjänstledig", "Ogiltig frånvaro", "Annat"];
  const [absences, setAbsences] = useState([]);
  const [newAbsence, setNewAbsence] = useState({ staffId: "", type: "Semester", from: today(), to: today() });
  const addAbsence = async () => {
    if (!newAbsence.from || !newAbsence.to || newAbsence.from > newAbsence.to) return;
    if (!newAbsence.staffId) return;
    try {
      const svar = await api.addAbsence(newAbsence);
      setAbsences((prev) => [...prev, svar.absence]);
    } catch (err) {
      setDataError(err.message);
    }
  };
  const removeAbsence = async (id) => {
    try {
      await api.removeAbsence(id);
      setAbsences((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      setDataError(err.message);
    }
  };
  const overlapDays = (aFrom, aTo, rFrom, rTo) => {
    const start = aFrom > rFrom ? aFrom : rFrom;
    const end = aTo < rTo ? aTo : rTo;
    if (start > end) return 0;
    return Math.round((new Date(end + "T00:00:00") - new Date(start + "T00:00:00")) / 86400000) + 1;
  };

  const addOrder = async () => {
    if (!newOrder.kund.trim() || !newOrder.bil.trim()) return;
    try {
      const svar = await api.createOrder({
        kund: newOrder.kund.trim(),
        bil: newOrder.bil.trim(),
        tjanst: newOrder.tjanst.trim(),
        staffId: newOrder.staffId || null,
        datum: newOrder.datum,
        tid: newOrder.tid,
        timmar: Number(newOrder.timmar) || 1,
      });
      const ny = fromApiOrder(svar.order);
      setOrders((prev) => [...prev, ny]);
      setDocOrderId(ny.id);
      setNewOrder({ kund: "", bil: "", tjanst: "", staffId: staff[0]?.id ?? "", datum: today(), tid: "09:00", timmar: 1 });
    } catch (err) {
      setDataError(err.message);
    }
  };

  // --- Ändra och ta bort ordrar ---
  // Bara arbetsledare, platschef och ägare. Servern kontrollerar samma sak —
  // det här styr bara om knapparna syns.
  const [redigerarOrder, setRedigerarOrder] = useState(null);

  const startaRedigering = (o) => {
    setRedigerarOrder({
      id: o.id,
      kund: o.kund,
      bil: o.bil,
      tjanst: o.tjanst ?? "",
      staffId: o.staffId ?? "",
      datum: o.datum,
      tid: o.tid,
      timmar: o.timmar,
    });
  };

  const sparaRedigering = async () => {
    const f = redigerarOrder;
    if (!f.kund.trim() || !f.bil.trim()) {
      setDataError("Kund och bil måste fyllas i.");
      return;
    }
    try {
      const svar = await api.updateOrder(f.id, {
        kund: f.kund.trim(),
        bil: f.bil.trim(),
        tjanst: f.tjanst.trim(),
        staffId: f.staffId || null,
        datum: f.datum,
        tid: f.tid,
        timmar: Number(f.timmar) || 1,
      });
      replaceOrder(svar.order);
      setRedigerarOrder(null);
      setDataError("");
    } catch (err) {
      setDataError(err.message);
    }
  };

  /// Kunden får ändra och avboka fram tills verkstaden startat arbetet.
  /// Servern kontrollerar samma sak — det här styr bara om knapparna syns.
  const kundFarAndra = (o) => ["ny", "mottagen"].includes(o.bookingStatus);

  const [kundRedigerar, setKundRedigerar] = useState(null);

  const sparaKundandring = async () => {
    const f = kundRedigerar;
    if (!f.bil.trim()) {
      setDataError("Bil måste fyllas i.");
      return;
    }
    try {
      const svar = await api.updateOrder(f.id, {
        bil: f.bil.trim(),
        tjanst: f.tjanst.trim(),
        hamtning: { mode: f.hamtningMode, datum: f.hamtningDatum || undefined, tid: f.hamtningTid || undefined },
        aterlamning: { mode: f.aterlamningMode, datum: f.aterlamningDatum || undefined, tid: f.aterlamningTid || undefined },
      });
      replaceOrder(svar.order);
      setKundRedigerar(null);
      setDataError("");
    } catch (err) {
      setDataError(err.message);
    }
  };

  const avbokaSomKund = async (o) => {
    const ok = window.confirm(
      `Avboka ${o.bil}?\n\nBokningen tas bort och verkstaden ser den inte längre.\nDet går inte att ångra.`
    );
    if (!ok) return;
    try {
      await api.deleteOrder(o.id);
      setOrders((prev) => prev.filter((x) => x.id !== o.id));
      setDataError("");
    } catch (err) {
      setDataError(err.message);
    }
  };

  const taBortOrder = async (o) => {
    // Vad som faktiskt försvinner räknas fram här och visas i frågan. Loggad
    // arbetstid ingår i löneunderlaget — den som raderar ska veta det innan,
    // inte upptäcka det när rapporten inte stämmer.
    const minuter = Math.round(
      [...o.steps, ...o.extraSteps].reduce((s, x) => s + stepElapsedMs(x), 0) / 60000
    );
    const delar = [
      `${o.steps.length + o.extraSteps.length} moment`,
      minuter > 0 ? `${minuter} loggade minuter` : null,
      o.photos.length > 0 ? `${o.photos.length} foton` : null,
      (o.chat || []).length > 0 ? `${o.chat.length} meddelanden` : null,
    ].filter(Boolean);

    const ok = window.confirm(
      `Ta bort ordern ${o.kund} – ${o.bil}?\n\n` +
      `Detta försvinner: ${delar.join(", ")}.\n\n` +
      (minuter > 0
        ? "Den loggade tiden räknas bort ur rapporter och löneunderlag.\n\n"
        : "") +
      "Det går inte att ångra."
    );
    if (!ok) return;

    try {
      await api.deleteOrder(o.id);
      setOrders((prev) => prev.filter((x) => x.id !== o.id));
      setDataError("");
    } catch (err) {
      setDataError(err.message);
    }
  };

  const addPhoto = (orderId, file) => {
    const reader = new FileReader();
    reader.onload = () => {
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, photos: [...o.photos, { id: uid(), src: reader.result }] } : o)));
    };
    reader.readAsDataURL(file);
  };
  const updateNote = (orderId, text) => setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, notes: text } : o)));

  const submitBooking = async () => {
    if (!bookingForm.bil.trim()) return;
    try {
      // Fritextönskemål skickas som tillval, så att verkstaden ser dem som
      // egna moment på ordern i stället för att de tappas bort vid inskick.
      const svar = await api.createOrder({
        bil: bookingForm.bil.trim(),
        tjanst: bookingForm.info.trim(),
        notes: bookingForm.kontakt ? `Kontakt: ${bookingForm.kontakt}` : "",
        baseSteps: stepLabels.slice(0, 3).filter((n) => bookingForm.services.includes(n)),
        extras: [
          ...extraCatalog.filter((n) => bookingForm.services.includes(n)),
          ...customServices,
        ],
        datum: bookingForm.hamtningMode === "datum" && bookingForm.hamtningDatum
          ? bookingForm.hamtningDatum
          : today(),
        tid: bookingForm.hamtningMode === "datum" ? bookingForm.hamtningTid || "09:00" : "09:00",
        hamtning: {
          mode: bookingForm.hamtningMode,
          datum: bookingForm.hamtningDatum || undefined,
          tid: bookingForm.hamtningTid || undefined,
        },
        aterlamning: {
          mode: bookingForm.aterlamningMode,
          datum: bookingForm.aterlamningDatum || undefined,
          tid: bookingForm.aterlamningTid || undefined,
        },
      });
      setOrders((prev) => [...prev, fromApiOrder(svar.order)]);
      setBookingForm({ bil: "", info: "", kontakt: loggedInContact?.name ?? "", services: [], hamtningMode: "normal", hamtningDatum: "", hamtningTid: "", aterlamningMode: "normal", aterlamningDatum: "", aterlamningTid: "" });
      setTab("mina-bokningar");
    } catch (err) {
      setDataError(err.message);
    }
  };
  const advanceBooking = async (orderId, status) => {
    try {
      // klarAt sätts av servern, inte här — det är den tidsstämpeln som
      // nedräkningen mot automatisk kvittens utgår ifrån.
      const svar = await api.setOrderStatus(orderId, status);
      syncClock(svar.serverNow);
      replaceOrder(svar.order);
    } catch (err) {
      setDataError(err.message);
    }
  };

  /// Kunden kvitterar att bilen är mottagen och arbetet ser bra ut.
  const kvitteraOrder = async (orderId) => {
    try {
      const svar = await api.kvittera(orderId);
      syncClock(svar.serverNow);
      replaceOrder(svar.order);
    } catch (err) {
      setDataError(err.message);
    }
  };

  /// Antal hela timmar kvar tills ordern kvitteras automatiskt.
  const timmarTillAutoKvittens = (o) =>
    Math.max(0, Math.ceil((o.klarAt + KVITTENS_AUTO_MS - serverTime()) / 3600000));

  const setChatText = (orderId, text) => setChatDraft((prev) => ({ ...prev, [orderId]: { ...(prev[orderId] || { image: null }), text } }));
  const attachChatImage = (orderId, file) => {
    const reader = new FileReader();
    reader.onload = () => setChatDraft((prev) => ({ ...prev, [orderId]: { ...(prev[orderId] || { text: "" }), image: reader.result } }));
    reader.readAsDataURL(file);
  };
  // senderOrg är företaget eller verkstaden meddelandet kommer från, senderName
  // personen som skrev det. Namnet sparas ner på meddelandet i stället för att
  // slås upp vid visning — annars skulle historiken skriva om sig själv den dag
  // en kontaktperson byter namn eller slutar.
  //
  // Avsändaren sätts av servern utifrån vem som är inloggad — den skickas
  // medvetet inte med från klienten, som annars kunde signera i någon annans namn.
  const sendChatMessage = async (orderId) => {
    const draft = chatDraft[orderId] || { text: "", image: null };
    if (!draft.text.trim()) return;
    try {
      const svar = await api.sendMessage(orderId, draft.text.trim());
      replaceOrder(svar.order);
      setChatDraft((prev) => ({ ...prev, [orderId]: { text: "", image: null } }));
    } catch (err) {
      setDataError(err.message);
    }
  };

  /// Markerar orderns meddelanden som lästa för den inloggade.
  const markChatRead = async (orderId) => {
    const o = orders.find((x) => x.id === orderId);
    const falt = isForetag ? "readByCustomer" : "readByStaff";
    // Inget att göra om allt redan är läst — annars skulle varje omritning
    // skicka ett anrop.
    if (!o || !(o.chat || []).some((m) => !m[falt])) return;

    setOrders((prev) => prev.map((x) => (
      x.id === orderId
        ? { ...x, chat: (x.chat || []).map((m) => ({ ...m, [falt]: true })) }
        : x
    )));
    try {
      await api.markMessagesRead(orderId);
    } catch {
      // Ett misslyckat läskvitto är inte värt att avbryta något för.
    }
  };
  const markChatReadByStaff = markChatRead;
  const markChatReadByCustomer = markChatRead;

  const getLog = (staffId) => timeLogs.find((t) => t.staffId === staffId && t.datum === today());
  const updateLogField = (staffId, field, value) => setTimeLogs((prev) => prev.map((t) => (t.staffId === staffId && t.datum === today() ? { ...t, [field]: value } : t)));
  const clockIn = (staffId) => {
    setTimeLogs((prev) => {
      if (getLog(staffId)) return prev;
      return [...prev, { id: uid(), staffId, datum: today(), in: timeNow(), out: null, breaks: [] }];
    });
  };
  const clockOut = (staffId) => setTimeLogs((prev) => prev.map((t) => (t.staffId === staffId && t.datum === today() ? { ...t, out: timeNow() } : t)));
  const startBreak = (staffId) => setTimeLogs((prev) => prev.map((t) => (t.staffId === staffId && t.datum === today() ? { ...t, breaks: [...t.breaks, { start: timeNow(), end: null }] } : t)));
  const endBreak = (staffId) => setTimeLogs((prev) => prev.map((t) => {
    if (t.staffId === staffId && t.datum === today()) {
      const breaks = [...t.breaks];
      const last = breaks[breaks.length - 1];
      if (last && !last.end) breaks[breaks.length - 1] = { ...last, end: timeNow() };
      return { ...t, breaks };
    }
    return t;
  }));

  const workedHours = (log) => {
    if (!log || !log.in) return 0;
    const end = log.out || timeNow();
    let mins = toMinutes(end) - toMinutes(log.in);
    const breakMins = log.breaks.reduce((sum, b) => sum + (b.end ? toMinutes(b.end) - toMinutes(b.start) : 0), 0);
    return Math.max(0, (mins - breakMins) / 60);
  };

  const clockState = (staffId) => {
    const log = getLog(staffId);
    if (!log || !log.in) return "ute";
    if (log.out) return "klar";
    const onBreak = log.breaks.length > 0 && !log.breaks[log.breaks.length - 1].end;
    return onBreak ? "rast" : "arbetar";
  };

  const todaysOrders = orders.filter((o) => o.datum === today());
  const docOrder = orders.find((o) => o.id === docOrderId);

  const payroll = staff.map((s) => {
    const hours = timeLogs.filter((t) => t.staffId === s.id).reduce((sum, t) => sum + workedHours(t), 0);
    const rounded = Math.round(hours * 10) / 10;
    return { ...s, hours: rounded, pay: Math.round(rounded * s.rate) };
  });

  const bonusEvents = [];
  orders.filter((o) => o.datum >= reportFrom && o.datum <= reportTo).forEach((o) => {
    const doneSteps = o.steps.filter((step) => step.done);
    doneSteps.forEach((step) => {
      const price = getPrice(step.name);
      const minuter = Math.round(stepElapsedMs(step) / 60000);
      if (step.staffIds.length === 0) {
        bonusEvents.push({ staffId: null, datum: o.datum, bil: o.bil, kund: o.kund, moment: step.name, minuter, price, bonus: price });
      } else {
        const perPerson = price / step.staffIds.length;
        step.staffIds.forEach((staffId) => {
          bonusEvents.push({ staffId, datum: o.datum, bil: o.bil, kund: o.kund, moment: step.name, minuter, price, bonus: perPerson });
        });
      }
    });
  });
  const unassignedBonus = bonusEvents.filter((e) => !e.staffId).reduce((sum, e) => sum + e.bonus, 0);

  const report = staff.map((s) => {
    const items = bonusEvents.filter((e) => e.staffId === s.id);
    return { ...s, items, count: items.length, bonus: items.reduce((sum, e) => sum + e.bonus, 0) };
  });

  const ordersInRange = orders.filter((o) => o.datum >= reportFrom && o.datum <= reportTo);
  const orderStatusCounts = { vantar: 0, pagar: 0, klar: 0 };
  ordersInRange.forEach((o) => { orderStatusCounts[getOrderStatus(o)]++; });

  const bookingsInRange = orders.filter((o) => o.company && o.datum >= reportFrom && o.datum <= reportTo);
  const bookingStatusCounts = { ny: 0, mottagen: 0, pagar: 0, klar: 0 };
  bookingsInRange.forEach((o) => { bookingStatusCounts[o.bookingStatus]++; });
  const bookingsByCompany = {};
  bookingsInRange.forEach((o) => { bookingsByCompany[o.company] = (bookingsByCompany[o.company] || 0) + 1; });

  const absencesInRange = absences.filter((a) => overlapDays(a.from, a.to, reportFrom, reportTo) > 0);
  const attendanceReport = staff.map((s) => {
    const logsInRange = timeLogs.filter((t) => t.staffId === s.id && t.datum >= reportFrom && t.datum <= reportTo);
    const hours = Math.round(logsInRange.reduce((sum, t) => sum + workedHours(t), 0) * 10) / 10;
    const shifts = logsInRange.length;
    const byType = {};
    ABSENCE_TYPES.forEach((t) => { byType[t] = 0; });
    absences.filter((a) => a.staffId === s.id).forEach((a) => {
      byType[a.type] = (byType[a.type] || 0) + overlapDays(a.from, a.to, reportFrom, reportTo);
    });
    return { ...s, hours, shifts, byType };
  });

  const customerStats = (() => {
    const map = {};
    ordersInRange.forEach((o) => {
      if (!map[o.kund]) map[o.kund] = { kund: o.kund, orders: 0, momentsDone: 0, tjanstCounts: {} };
      map[o.kund].orders++;
      map[o.kund].momentsDone += [...o.steps, ...o.extraSteps].filter((s) => s.done).length;
      const t = o.tjanst || "Okänt";
      map[o.kund].tjanstCounts[t] = (map[o.kund].tjanstCounts[t] || 0) + 1;
    });
    return Object.values(map)
      .map((c) => {
        const top = Object.entries(c.tjanstCounts).sort((a, b) => b[1] - a[1])[0];
        return { ...c, topTjanst: top ? top[0] : "-" };
      })
      .sort((a, b) => b.orders - a.orders);
  })();

  const customerOrders = statCustomerView ? ordersInRange.filter((o) => o.kund === statCustomerView) : [];
  const customerMomentsDone = customerOrders.reduce((sum, o) => sum + [...o.steps, ...o.extraSteps].filter((s) => s.done).length, 0);
  const customerAvgMoments = customerOrders.length ? Math.round((customerMomentsDone / customerOrders.length) * 10) / 10 : 0;
  const customerMomentPopularity = (() => {
    const counts = {};
    customerOrders.forEach((o) => [...o.steps, ...o.extraSteps].forEach((s) => { if (s.done) counts[s.name] = (counts[s.name] || 0) + 1; }));
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  })();
  const customerMaxMoment = customerMomentPopularity.length ? customerMomentPopularity[0][1] : 1;
  const customerStatusCounts = { vantar: 0, pagar: 0, klar: 0 };
  customerOrders.forEach((o) => { customerStatusCounts[getOrderStatus(o)]++; });

  const totalMomentsDone = ordersInRange.reduce((sum, o) => sum + [...o.steps, ...o.extraSteps].filter((s) => s.done).length, 0);
  const avgMomentsPerOrder = ordersInRange.length ? Math.round((totalMomentsDone / ordersInRange.length) * 10) / 10 : 0;
  const momentPopularity = (() => {
    const counts = {};
    ordersInRange.forEach((o) => [...o.steps, ...o.extraSteps].forEach((s) => { if (s.done) counts[s.name] = (counts[s.name] || 0) + 1; }));
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  })();
  const maxMomentCount = momentPopularity.length ? momentPopularity[0][1] : 1;
  const bookingShare = ordersInRange.length ? Math.round((bookingsInRange.length / ordersInRange.length) * 100) : 0;
  const statusShare = {
    vantar: ordersInRange.length ? Math.round((orderStatusCounts.vantar / ordersInRange.length) * 100) : 0,
    pagar: ordersInRange.length ? Math.round((orderStatusCounts.pagar / ordersInRange.length) * 100) : 0,
    klar: ordersInRange.length ? Math.round((orderStatusCounts.klar / ordersInRange.length) * 100) : 0,
  };

  const downloadCSV = (filename, headers, rows) => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [headers.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  /// Etiketten ovanför ett meddelande: "Jonas Petersson · Bilcenter AB".
  /// Faller tillbaka på bara företagsnamnet för meddelanden skrivna innan
  /// avsändarnamnet började sparas, och på det gamla sender-fältet för
  /// meddelanden som ligger kvar från tidigare version.
  const chatSenderLabel = (m) => {
    const org = m.senderOrg ?? m.sender;
    return m.senderName ? `${m.senderName} · ${org}` : org;
  };

  const renderChat = (order, senderOrg, unreadCount = 0, senderName = null) => {
    const draft = chatDraft[order.id] || { text: "", image: null };
    return (
      <div style={{ marginTop: 12, borderTop: "1px solid #EEF1F7", paddingTop: 12 }}>
        {unreadCount > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, color: "#FF8A34", fontSize: 12, fontWeight: 600 }}>
            <span style={styles.dot("#FF8A34")} />{unreadCount} {unreadCount === 1 ? "nytt meddelande" : "nya meddelanden"}
          </div>
        )}
        <div style={{ maxHeight: 180, overflowY: "auto", marginBottom: 10, display: "flex", flexDirection: "column", gap: 10 }}>
          {(order.chat || []).map((m) => (
            <div key={m.id} style={{ fontSize: 12 }}>
              <div style={{ color: "#6B7789", marginBottom: 2 }}>{chatSenderLabel(m)} · {m.at}</div>
              {m.text && <div style={{ color: "#475467" }}>{m.text}</div>}
              {m.image && <img src={m.image} alt="bild" style={{ width: 84, height: 84, objectFit: "cover", border: "1px solid #D7E0EE", marginTop: 4 }} />}
            </div>
          ))}
          {(!order.chat || order.chat.length === 0) && <div style={{ fontSize: 12, color: "#6B7789" }}>Inga meddelanden än.</div>}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input style={{ ...styles.input, flex: 1, minWidth: 140 }} placeholder="Skriv ett meddelande..." value={draft.text} onChange={(e) => setChatText(order.id, e.target.value)} />
          <label style={{ ...styles.btnGhost, cursor: "pointer" }}>
            <ImagePlus size={14} />
            <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => e.target.files[0] && attachChatImage(order.id, e.target.files[0])} />
          </label>
          <button style={styles.btn} onClick={() => sendChatMessage(order.id)}>Skicka</button>
        </div>
        {draft.image && <div style={{ fontSize: 11, color: "#1657C8", marginTop: 4 }}>Bild bifogad ✓</div>}
      </div>
    );

  };

  const newBookingsCount = orders.filter((o) => o.company && o.bookingStatus === "ny").length;
  const unreadStaffMsgCount = orders.filter((o) => o.company).reduce((sum, o) => sum + (o.chat || []).filter((m) => !m.readByStaff).length, 0);
  const unreadCustomerMsgCount = orders.filter((o) => o.company === companyName).reduce((sum, o) => sum + (o.chat || []).filter((m) => !m.readByCustomer).length, 0);
  const staffNotifCount = newBookingsCount + unreadStaffMsgCount;

  const playNotificationSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.18, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.4);
    } catch (e) {
      // audio not available in this environment — fail silently
    }
  };

  const prevBookingCountRef = useRef(newBookingsCount);
  useEffect(() => {
    if (!isForetag && soundOn && newBookingsCount > prevBookingCountRef.current) {
      playNotificationSound();
    }
    prevBookingCountRef.current = newBookingsCount;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newBookingsCount]);

  const unreadNewsCount = announcements.filter((a) => !a.read).length;
  const publishAnnouncement = async () => {
    if (!newAnnouncement.title.trim() || !newAnnouncement.text.trim()) return;
    try {
      const svar = await api.addAnnouncement({
        title: newAnnouncement.title.trim(),
        text: newAnnouncement.text.trim(),
      });
      setAnnouncements((prev) => [svar.announcement, ...prev]);
      setNewAnnouncement({ title: "", text: "" });
    } catch (err) {
      setDataError(err.message);
    }
  };

  const sendCustomerMessage = async () => {
    if (!newCustomerMessage.title.trim() || !newCustomerMessage.text.trim()) return;
    try {
      const svar = await api.addCustomerMessage({
        title: newCustomerMessage.title.trim(),
        text: newCustomerMessage.text.trim(),
        target: newCustomerMessage.target,
      });
      setCustomerMessages((prev) => [svar.message, ...prev]);
      setNewCustomerMessage({ title: "", text: "", target: "alla" });
    } catch (err) {
      setDataError(err.message);
    }
  };

  const scheduleMeeting = async () => {
    if (!newMeeting.title.trim() || !newMeeting.datum || !newMeeting.tid) return;
    try {
      const svar = await api.addMeeting({
        title: newMeeting.title.trim(),
        type: newMeeting.type,
        target: newMeeting.target,
        datum: newMeeting.datum,
        tid: newMeeting.tid,
      });
      setMeetings((prev) => [...prev, svar.meeting]);
      setNewMeeting({ title: "", type: "Uppföljning", target: "alla", datum: today(), tid: "10:00" });
    } catch (err) {
      setDataError(err.message);
    }
  };

  const removeMeeting = async (id) => {
    try {
      await api.removeMeeting(id);
      setMeetings((prev) => prev.filter((m) => m.id !== id));
    } catch (err) {
      setDataError(err.message);
    }
  };

  // Läskvittot på nyheter sparas per person på servern, så att en läsning inte
  // döljer nyheten för alla andra.
  useEffect(() => {
    if (tab !== "nyheter" || unreadNewsCount === 0) return;
    setAnnouncements((prev) => prev.map((a) => (a.read ? a : { ...a, read: true })));
    api.markAnnouncementsRead().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, unreadNewsCount]);

  const prevNewsCountRef = useRef(announcements.length);
  useEffect(() => {
    if (!isForetag && soundOn && announcements.length > prevNewsCountRef.current) {
      playNotificationSound();
    }
    prevNewsCountRef.current = announcements.length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [announcements.length]);

  // Läskvittona kommer från servern och är per person, precis som för
  // personalnyheterna. Servern filtrerar redan bort andra företags utskick, så
  // här räknas allt som kommit tillbaka.
  const unreadCustomerMessages = customerMessages.filter((m) => !m.read).length;
  const unseenMeetings = meetings.filter((m) => !m.seen).length;

  useEffect(() => {
    if (tab === "kundmeddelanden" && unreadCustomerMessages > 0) {
      setCustomerMessages((prev) => prev.map((m) => (m.read ? m : { ...m, read: true })));
      api.markCustomerMessagesRead().catch(() => {});
    }
    if (tab === "kundmoten" && unseenMeetings > 0) {
      setMeetings((prev) => prev.map((m) => (m.seen ? m : { ...m, seen: true })));
      api.markMeetingsSeen().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, unreadCustomerMessages, unseenMeetings]);

  const prevCustomerMsgCountRef = useRef(unreadCustomerMessages);
  useEffect(() => {
    if (isForetag && soundOn && unreadCustomerMessages > prevCustomerMsgCountRef.current) {
      playNotificationSound();
    }
    prevCustomerMsgCountRef.current = unreadCustomerMessages;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadCustomerMessages]);

  const prevMeetingsCountRef = useRef(unseenMeetings);
  useEffect(() => {
    if (isForetag && soundOn && unseenMeetings > prevMeetingsCountRef.current) {
      playNotificationSound();
    }
    prevMeetingsCountRef.current = unseenMeetings;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unseenMeetings]);

  const nav = [
    { id: "oversikt", label: "Översikt", icon: LayoutGrid },
    { id: "nyheter", label: "Nyheter", icon: Megaphone, badge: unreadNewsCount },
    { id: "personal", label: "Personal", icon: Users },
    { id: "schema", label: "Schema", icon: CalendarDays },
    { id: "tidrapport", label: "Stämpelklocka", icon: Timer },
    { id: "ordrar", label: "Ordrar", icon: ClipboardList },
    { id: "bokningar", label: "Bokningar", icon: Building2, badge: staffNotifCount },
    { id: "kundkontakt", label: "Kundkontakt", icon: Radio },
    { id: "planering", label: "Live-planering", icon: CalendarClock },
    { id: "rapport", label: "Rapport", icon: BarChart3 },
    { id: "statistik", label: "Statistik", icon: BarChart3 },
    { id: "loner", label: "Löner", icon: Wallet },
    { id: "dok", label: "Dokumentation", icon: Camera },
    { id: "installningar", label: "Inställningar", icon: Settings },
    { id: "konton", label: "Konton", icon: ShieldCheck },
    // Alla roller måste kunna byta sitt eget lösenord, inte bara de som ser
    // Inställningar.
    { id: "mitt-konto", label: "Mitt konto", icon: ShieldCheck },
  ].filter((n) => (n.id !== "loner" || canSeeLoner) && (n.id !== "rapport" || canManageStaff) && (n.id !== "installningar" || canManageStaff) && (n.id !== "statistik" || canManageStaff) && (n.id !== "konton" || role === "agare") && (n.id !== "kundkontakt" || canManageStaff));

  const portalNav = [
    { id: "mina-bokningar", label: "Mina bokningar", icon: ClipboardList, badge: unreadCustomerMsgCount },
    { id: "ny-bokning", label: "Ny bokning", icon: Plus },
    { id: "kundmeddelanden", label: "Meddelanden", icon: Radio, badge: unreadCustomerMessages },
    { id: "kundmoten", label: "Möten", icon: Video, badge: unseenMeetings },
    { id: "mitt-konto", label: "Mitt konto", icon: ShieldCheck },
  ];

  // Återställningslänken går före inloggningsrutan — den som klickat på den
  // kan per definition inte logga in.
  if (aterstall) {
    return (
      <div style={{ ...styles.page, alignItems: "center", justifyContent: "center" }}>
        <style>{fontImport}</style>
        <div style={{ width: "min(380px, 92vw)", background: "#FAFBFE", border: "1px solid #DEE6F3", padding: "32px 28px", boxShadow: "0 4px 16px rgba(16,24,40,0.08)" }}>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 30, color: "#1657C8" }}>GG</div>
          </div>

          {aterstall.fel ? (
            <>
              <p style={{ fontSize: 13, color: "#C1560A", textAlign: "center", lineHeight: 1.6 }}>{aterstall.fel}</p>
              <p style={{ fontSize: 12, color: "#6B7789", textAlign: "center", marginTop: 12 }}>
                Länkar gäller i en timme och bara en gång. Begär en ny från inloggningssidan.
              </p>
              <button style={{ ...styles.btnGhost, width: "100%", justifyContent: "center", marginTop: 16 }}
                onClick={() => { window.location.href = window.location.origin; }}>
                Till inloggningen
              </button>
            </>
          ) : aterstallKlar ? (
            <>
              <p style={{ fontSize: 13, color: "#1657C8", textAlign: "center", fontWeight: 600 }}>Lösenordet är ändrat.</p>
              <p style={{ fontSize: 12, color: "#6B7789", textAlign: "center", margin: "10px 0 18px", lineHeight: 1.6 }}>
                Logga in med <strong>{aterstallKlar}</strong> och ditt nya lösenord.
                Alla andra inloggningar har loggats ut.
              </p>
              <button style={{ ...styles.btn, width: "100%", justifyContent: "center" }}
                onClick={() => { window.location.href = window.location.origin; }}>
                Till inloggningen
              </button>
            </>
          ) : (
            <>
              <p style={{ fontSize: 13, color: "#1F2A44", textAlign: "center", margin: "0 0 4px" }}>
                Hej {aterstall.name}!
              </p>
              <p style={{ fontSize: 12, color: "#6B7789", textAlign: "center", margin: "0 0 20px", lineHeight: 1.6 }}>
                Välj ett nytt lösenord för {aterstall.email}.
              </p>
              <div style={{ marginBottom: 12 }}>
                <label style={styles.label}>Nytt lösenord (minst 8 tecken)</label>
                <input type="password" style={{ ...styles.input, width: "100%" }} value={aterstallForm.losen}
                  onChange={(e) => setAterstallForm({ ...aterstallForm, losen: e.target.value })} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={styles.label}>Upprepa lösenordet</label>
                <input type="password" style={{ ...styles.input, width: "100%" }} value={aterstallForm.upprepa}
                  onChange={(e) => setAterstallForm({ ...aterstallForm, upprepa: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") sattNyttLosenord(); }} />
              </div>
              {authError && <p style={{ fontSize: 12, color: "#C1560A", margin: "0 0 12px" }}>{authError}</p>}
              <button style={{ ...styles.btn, width: "100%", justifyContent: "center" }}
                onClick={sattNyttLosenord} disabled={authBusy}>
                {authBusy ? "Sparar..." : "Spara nytt lösenord"}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Inbjudningslänken går före allt annat — den som klickat på den har ännu
  // inget konto att logga in med.
  if (inbjudan) {
    return (
      <div style={{ ...styles.page, alignItems: "center", justifyContent: "center" }}>
        <style>{fontImport}</style>
        <div style={{ width: "min(400px, 92vw)", background: "#FAFBFE", border: "1px solid #DEE6F3", padding: "32px 28px", boxShadow: "0 4px 16px rgba(16,24,40,0.08)" }}>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 30, color: "#1657C8" }}>GG</div>
          </div>

          {inbjudan.fel ? (
            <>
              <p style={{ fontSize: 13, color: "#C1560A", textAlign: "center", lineHeight: 1.6 }}>{inbjudan.fel}</p>
              <p style={{ fontSize: 12, color: "#6B7789", textAlign: "center", marginTop: 12 }}>
                Be den som bjöd in dig att skicka en ny länk.
              </p>
            </>
          ) : inbjudanKlar ? (
            <>
              <p style={{ fontSize: 13, color: "#1657C8", textAlign: "center", fontWeight: 600 }}>Kontot är skapat.</p>
              <p style={{ fontSize: 12, color: "#6B7789", textAlign: "center", margin: "10px 0 18px", lineHeight: 1.6 }}>
                Logga in med <strong>{inbjudanKlar}</strong> och lösenordet du just valde.
              </p>
              <button style={{ ...styles.btn, width: "100%", justifyContent: "center" }}
                onClick={() => { window.location.href = window.location.origin; }}>
                Till inloggningen
              </button>
            </>
          ) : (
            <>
              <p style={{ fontSize: 13, color: "#1F2A44", textAlign: "center", margin: "0 0 4px" }}>
                Hej {inbjudan.name}!
              </p>
              <p style={{ fontSize: 12, color: "#6B7789", textAlign: "center", margin: "0 0 20px", lineHeight: 1.6 }}>
                Du har bjudits in till {inbjudan.verkstad}
                {inbjudan.company ? ` som kontaktperson för ${inbjudan.company}` : ""}.
                Välj ett lösenord så är du igång.
              </p>
              <div style={{ marginBottom: 12 }}>
                <label style={styles.label}>E-post</label>
                <input style={{ ...styles.input, width: "100%" }} value={inbjudan.email} disabled readOnly />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={styles.label}>Välj lösenord (minst 8 tecken)</label>
                <input type="password" style={{ ...styles.input, width: "100%" }} value={inbjudanForm.losen}
                  onChange={(e) => setInbjudanForm({ ...inbjudanForm, losen: e.target.value })} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={styles.label}>Upprepa lösenordet</label>
                <input type="password" style={{ ...styles.input, width: "100%" }} value={inbjudanForm.upprepa}
                  onChange={(e) => setInbjudanForm({ ...inbjudanForm, upprepa: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") acceptteraInbjudan(); }} />
              </div>
              {authError && <p style={{ fontSize: 12, color: "#C1560A", margin: "0 0 12px" }}>{authError}</p>}
              <button style={{ ...styles.btn, width: "100%", justifyContent: "center" }}
                onClick={acceptteraInbjudan} disabled={authBusy}>
                {authBusy ? "Skapar konto..." : "Skapa konto"}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Medan sessionen återupptas: visa ingenting hellre än att blinka förbi
  // inloggningsrutan för någon som faktiskt redan är inloggad.
  if (authLoading) {
    return (
      <div style={{ ...styles.page, alignItems: "center", justifyContent: "center" }}>
        <style>{fontImport}</style>
        <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 30, color: "#1657C8" }}>GG</div>
      </div>
    );
  }

  /// Formuläret för lösenordsbyte. Används både i den tvingande vyn och under
  /// Inställningar, så att fälten och valideringen inte finns i två versioner.
  const renderLosenordsformular = (knapptext) => (
    <>
      <div style={{ marginBottom: 12 }}>
        <label style={styles.label}>Nuvarande lösenord</label>
        <input type="password" style={{ ...styles.input, width: "100%" }} value={pwForm.current}
          onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} />
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={styles.label}>Nytt lösenord (minst 8 tecken)</label>
        <input type="password" style={{ ...styles.input, width: "100%" }} value={pwForm.ny}
          onChange={(e) => setPwForm({ ...pwForm, ny: e.target.value })} />
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={styles.label}>Upprepa nytt lösenord</label>
        <input type="password" style={{ ...styles.input, width: "100%" }} value={pwForm.upprepa}
          onChange={(e) => setPwForm({ ...pwForm, upprepa: e.target.value })}
          onKeyDown={(e) => { if (e.key === "Enter") bytLosenord(); }} />
      </div>
      {pwMelding.text && (
        <p style={{ fontSize: 12, margin: "0 0 12px", color: pwMelding.typ === "ok" ? "#1657C8" : "#C1560A" }}>
          {pwMelding.text}
        </p>
      )}
      <button style={{ ...styles.btn, width: "100%", justifyContent: "center" }}
        onClick={bytLosenord} disabled={pwBusy}>
        {pwBusy ? "Sparar..." : knapptext}
      </button>
    </>
  );

  // Ett lösenord som någon annan valt får inte bli permanent. Servern nekar
  // ändå alla andra anrop så länge flaggan är satt — det här är bara den
  // begripliga vägen ut.
  if (authStage === "in" && currentUser?.mustChangePassword) {
    return (
      <div style={{ ...styles.page, alignItems: "center", justifyContent: "center" }}>
        <style>{fontImport}</style>
        <div style={{ width: "min(380px, 92vw)", background: "#FAFBFE", border: "1px solid #DEE6F3", padding: "32px 28px", boxShadow: "0 4px 16px rgba(16,24,40,0.08)" }}>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 30, color: "#1657C8" }}>GG</div>
            <div style={{ fontSize: 13, color: "#1F2A44", fontWeight: 600, marginTop: 6 }}>Välj ett nytt lösenord</div>
            <p style={{ fontSize: 12, color: "#6B7789", margin: "8px 0 0", lineHeight: 1.5 }}>
              Ditt lösenord sattes av en administratör. Välj ett eget innan du fortsätter.
            </p>
          </div>
          {renderLosenordsformular("Spara och fortsätt")}
          <p style={{ fontSize: 11, color: "#94A3B8", textAlign: "center", marginTop: 14 }}>
            <span style={{ color: "#1657C8", cursor: "pointer" }} onClick={handleLogout}>Logga ut</span>
          </p>
        </div>
      </div>
    );
  }

  if (authStage !== "in") {
    return (
      <div style={{ ...styles.page, alignItems: "center", justifyContent: "center" }}>
        <style>{fontImport}</style>
        <div style={{ width: "min(360px, 92vw)", background: "#FAFBFE", border: "1px solid #DEE6F3", borderRadius: 4, padding: "32px 28px", boxShadow: "0 4px 16px rgba(16,24,40,0.08)" }}>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 30, color: "#1657C8" }}>GG</div>
            <div style={{ fontSize: 12, color: "#6B7789" }}>Logga in för att fortsätta</div>
          </div>

          {authStage === "login" ? (
            <>
              <div style={{ marginBottom: 12 }}>
                <label style={styles.label}>E-post</label>
                <input style={{ ...styles.input, width: "100%" }} value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} placeholder="namn@företag.se" />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={styles.label}>Lösenord</label>
                <input type="password" style={{ ...styles.input, width: "100%" }} value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} />
              </div>
              {authError && <p style={{ fontSize: 12, color: "#C1560A", margin: "0 0 12px" }}>{authError}</p>}
              <button style={{ ...styles.btn, width: "100%", justifyContent: "center" }} onClick={handleLogin} disabled={authBusy}>{authBusy ? "Loggar in..." : "Logga in"}</button>
              <p style={{ fontSize: 12, textAlign: "center", marginTop: 12 }}>
                <span style={{ color: "#1657C8", cursor: "pointer" }}
                  onClick={() => { setAuthError(""); setGlomtSvar(""); setGlomtEpost(loginForm.email); setAuthStage("glomt"); }}>
                  Glömt lösenordet?
                </span>
              </p>
              <p style={{ fontSize: 11, color: "#94A3B8", textAlign: "center", marginTop: 10 }}>Bilsäljare: logga in med er egen e-post — kopplas automatiskt till ert företags konto.</p>
              <p style={{ fontSize: 12, color: "#6B7789", textAlign: "center", marginTop: 16 }}>
                Är ni en företagskund utan konto?{" "}
                <span style={{ color: "#1657C8", cursor: "pointer", fontWeight: 600 }} onClick={() => { setAuthError(""); setAuthStage("register"); }}>Registrera företag</span>
              </p>
            </>
          ) : authStage === "glomt" ? (
            <>
              {glomtSvar ? (
                <>
                  <p style={{ fontSize: 13, color: "#1657C8", textAlign: "center", lineHeight: 1.6 }}>{glomtSvar}</p>
                  <button style={{ ...styles.btnGhost, width: "100%", justifyContent: "center", marginTop: 18 }}
                    onClick={() => { setAuthStage("login"); setGlomtSvar(""); }}>
                    Tillbaka till inloggningen
                  </button>
                </>
              ) : (
                <>
                  <p style={{ fontSize: 12, color: "#6B7789", textAlign: "center", margin: "0 0 18px", lineHeight: 1.6 }}>
                    Ange din e-postadress så skickar vi en länk där du kan välja ett nytt lösenord.
                  </p>
                  <div style={{ marginBottom: 16 }}>
                    <label style={styles.label}>E-post</label>
                    <input style={{ ...styles.input, width: "100%" }} value={glomtEpost}
                      onChange={(e) => setGlomtEpost(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") begarAterstallning(); }}
                      placeholder="namn@företag.se" />
                  </div>
                  {authError && <p style={{ fontSize: 12, color: "#C1560A", margin: "0 0 12px" }}>{authError}</p>}
                  <button style={{ ...styles.btn, width: "100%", justifyContent: "center" }}
                    onClick={begarAterstallning} disabled={authBusy}>
                    {authBusy ? "Skickar..." : "Skicka återställningslänk"}
                  </button>
                  <p style={{ fontSize: 12, textAlign: "center", marginTop: 14 }}>
                    <span style={{ color: "#1657C8", cursor: "pointer" }} onClick={() => { setAuthStage("login"); setAuthError(""); }}>
                      Tillbaka
                    </span>
                  </p>
                </>
              )}
            </>
          ) : authStage === "register" ? (
            <>
              <div style={{ marginBottom: 12 }}>
                <label style={styles.label}>Företagsnamn</label>
                <input style={{ ...styles.input, width: "100%" }} value={registerForm.company} onChange={(e) => setRegisterForm({ ...registerForm, company: e.target.value })} placeholder="t.ex. Bilcenter Kristianstad AB" />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={styles.label}>Kontaktperson</label>
                <input style={{ ...styles.input, width: "100%" }} value={registerForm.contact} onChange={(e) => setRegisterForm({ ...registerForm, contact: e.target.value })} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={styles.label}>E-post</label>
                <input style={{ ...styles.input, width: "100%" }} value={registerForm.email} onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })} placeholder="namn@företag.se" />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={styles.label}>Lösenord</label>
                <input type="password" style={{ ...styles.input, width: "100%" }} value={registerForm.password} onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })} />
              </div>
              {authError && <p style={{ fontSize: 12, color: "#C1560A", margin: "0 0 12px" }}>{authError}</p>}
              <button style={{ ...styles.btn, width: "100%", justifyContent: "center" }} onClick={handleRegister}>Skapa konto</button>
              <p style={{ fontSize: 12, color: "#6B7789", textAlign: "center", marginTop: 16 }}>
                Har ni redan ett konto?{" "}
                <span style={{ color: "#1657C8", cursor: "pointer", fontWeight: 600 }} onClick={() => { setAuthError(""); setAuthStage("login"); }}>Logga in</span>
              </p>
            </>
          ) : (
            <div style={{ textAlign: "center" }}>
              <div style={{ ...styles.dot("#FFC93C"), width: 12, height: 12, margin: "0 auto 14px" }} />
              <p style={{ fontSize: 14, color: "#1F2A44", fontWeight: 600, margin: "0 0 8px" }}>Väntar på godkännande</p>
              <p style={{ fontSize: 13, color: "#6B7789", margin: "0 0 20px" }}>Tack för er registrering, {companyName}! En admin behöver godkänna kontot innan ni kan logga in och se era bokningar.</p>
              <span style={{ color: "#1657C8", cursor: "pointer", fontWeight: 600, fontSize: 13 }} onClick={() => { setAuthError(""); setAuthStage("login"); }}>Tillbaka till inloggning</span>
            </div>
          )}
          <p style={{ fontSize: 11, color: "#94A3B8", textAlign: "center", marginTop: 20 }}>Personal bjuds in av sin arbetsledare/ägare — kontakta dem för inloggning.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <style>{fontImport}</style>
      {isMobile && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: 52, background: "#1657C8", display: "flex", alignItems: "center", gap: 10, padding: "0 14px", zIndex: 60 }}>
          <button
            style={{ background: "transparent", border: "none", color: "#FFFFFF", cursor: "pointer", padding: 4, display: "flex" }}
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label="Meny"
          >
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
          <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 18, color: "#FFFFFF" }}>GG</span>
          <span style={{ fontSize: 12, color: "rgba(255,255,255,0.75)" }}>{shopName}</span>
        </div>
      )}
      {isMobile && mobileMenuOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.4)", zIndex: 55 }} onClick={() => setMobileMenuOpen(false)} />
      )}
      <div style={isMobile ? { ...styles.sidebar, position: "fixed", top: 0, left: 0, height: "100vh", zIndex: 58, transform: mobileMenuOpen ? "translateX(0)" : "translateX(-100%)", transition: "transform 0.25s ease", overflowY: "auto", paddingTop: 64 } : styles.sidebar}>
        <div style={styles.brand}>GG</div>
        <div style={styles.brandSub}>{shopName}{shopSubtitle ? " · " + shopSubtitle : ""}</div>
        <div style={{ padding: "0 20px 16px" }}>
          <div style={{ fontSize: 13, color: "#FFFFFF", fontWeight: 600 }}>{currentUser?.name}</div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", marginTop: 2 }}>
            {ROLE_LABELS[role] ?? role}
            {isForetag && companyName ? ` · ${companyName}` : ""}
          </div>

          {arAgare && mojligaKonton.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <label style={styles.label}>Agera som</label>
              <select
                style={{ ...styles.input, width: "100%" }}
                value={agerarSom ? currentUser?.id ?? "" : ""}
                onChange={(e) => agerSom(e.target.value)}
              >
                <option value="">Mig själv</option>
                {mojligaKonton.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.name} — {ROLE_LABELS[k.role] ?? k.role}
                    {k.companyName ? ` (${k.companyName})` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        {!isPlatform && (
          <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 20px 14px", color: "rgba(255,255,255,0.8)", fontSize: 12, cursor: "pointer" }}>
            <input type="checkbox" checked={soundOn} onChange={(e) => setSoundOn(e.target.checked)} />
            {isForetag ? "Ljudnotis vid nytt meddelande/möte" : "Ljudnotis vid ny bokning"}
          </label>
        )}
        {(!isForetag && !isPlatform && staffNotifCount > 0) && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 20px 16px", color: "#FFC93C" }}>
            <Bell size={15} />
            <span style={{ fontSize: 12, fontWeight: 600 }}>{staffNotifCount} nytt — {newBookingsCount > 0 ? `${newBookingsCount} ny${newBookingsCount > 1 ? "a" : ""} bokning${newBookingsCount > 1 ? "ar" : ""}` : ""}{newBookingsCount > 0 && unreadStaffMsgCount > 0 ? " · " : ""}{unreadStaffMsgCount > 0 ? `${unreadStaffMsgCount} meddelande${unreadStaffMsgCount > 1 ? "n" : ""}` : ""}</span>
          </div>
        )}
        {(isForetag && unreadCustomerMsgCount > 0) && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 20px 16px", color: "#FFC93C" }}>
            <Bell size={15} />
            <span style={{ fontSize: 12, fontWeight: 600 }}>{unreadCustomerMsgCount} nytt meddelande{unreadCustomerMsgCount > 1 ? "n" : ""}</span>
          </div>
        )}
        {(isForetag ? portalNav : isPlatform ? [{ id: "plattform", label: "Plattform", icon: ShieldCheck }] : nav).map((n) => (
          <div key={n.id} style={styles.navItem(tab === n.id)} onClick={() => { setTab(n.id); setMobileMenuOpen(false); }}>
            <n.icon size={16} />
            {n.label}
            {!!n.badge && <span style={styles.navBadge}>{n.badge}</span>}
          </div>
        ))}
        <div style={{ marginTop: "auto" }} />
        <div
          style={{ ...styles.navItem(false), color: "rgba(255,255,255,0.7)" }}
          onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
        >
          <LogOut size={16} />
          Logga ut
        </div>
      </div>

      <div style={isMobile ? { ...styles.main, padding: "16px 14px", paddingTop: 68, width: "100%" } : styles.main}>
        {/* Vyläget måste synas hela tiden. Glömmer man att man tittar som
            någon annan blir varje "varför ser jag inte X" obegriplig. */}
        {/* Länken går bara att se här och nu. Servern lagrar ett hash av
            token och kan aldrig visa den igen. */}
        {senasteLank && (
          <div style={{ background: "#E4EEFD", border: "1px solid #9FC7FB", padding: "14px 16px", marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#1657C8", marginBottom: 6 }}>
              Inbjudan skapad för {senasteLank.namn} ({senasteLank.epost})
            </div>

            {senasteLank.mejl?.skickat ? (
              <p style={{ fontSize: 12, color: "#1657C8", margin: "0 0 10px", lineHeight: 1.5 }}>
                <Check size={13} style={{ verticalAlign: -2 }} /> <strong>Mejlet är skickat</strong> till {senasteLank.epost}.
                Länken nedan är samma som i mejlet — spara den om du vill kunna skicka på annat sätt,
                till exempel om mejlet fastnar i skräpposten.
              </p>
            ) : (
              <p style={{ fontSize: 12, color: "#B5720A", margin: "0 0 10px", lineHeight: 1.5 }}>
                <strong>Inget mejl skickades.</strong>{senasteLank.mejl?.orsak ? ` ${senasteLank.mejl.orsak}` : ""}
                {" "}Skicka länken själv — via SMS, WhatsApp eller ditt mejlprogram.
              </p>
            )}

            <p style={{ fontSize: 12, color: "#475467", margin: "0 0 10px", lineHeight: 1.5 }}>
              Personen väljer sitt eget lösenord. Länken gäller i 14 dagar
              och <strong>går inte att se igen</strong> när du stänger den här rutan.
            </p>
            <input
              readOnly
              value={senasteLank.lank}
              onFocus={(e) => e.target.select()}
              style={{ ...styles.input, width: "100%", fontSize: 12, background: "#FFFFFF" }}
            />
            <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
              <button style={styles.btn} onClick={() => {
                navigator.clipboard?.writeText(senasteLank.lank);
                setDataError("");
              }}>Kopiera länk</button>
              <a style={{ ...styles.btnGhost, textDecoration: "none" }}
                 href={`mailto:${senasteLank.epost}?subject=${encodeURIComponent("Inbjudan till " + shopName)}&body=${encodeURIComponent("Hej " + senasteLank.namn + ",\n\nHär är din länk för att skapa ett konto:\n" + senasteLank.lank + "\n\nLänken gäller i 14 dagar.")}`}>
                Öppna i mejlprogram
              </a>
              <button style={styles.btnGhost} onClick={() => setSenasteLank(null)}>Stäng</button>
            </div>
          </div>
        )}

        {agerarSom && (
          <div style={{ background: "#C1560A", color: "#FFFFFF", padding: "10px 14px", marginBottom: 16, fontSize: 13, fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <span>
              Du agerar som <strong>{agerarSom.name}</strong> ({ROLE_LABELS[agerarSom.role] ?? agerarSom.role}
              {agerarSom.companyName ? ` · ${agerarSom.companyName}` : ""}).
              {" "}Allt du gör ser ut som att {agerarSom.name} gjorde det.
            </span>
            <button style={{ background: "#FFFFFF", color: "#C1560A", border: "none", padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }} onClick={() => agerSom(null)}>
              Tillbaka till mig själv
            </button>
          </div>
        )}

        {/* Fel från servern måste synas. Utan detta misslyckas ett sparande
            tyst, och användaren tror att ändringen gick igenom. */}
        {dataError && (
          <div style={{ background: "#FFF3D6", border: "1px solid #FFD98A", color: "#B5720A", padding: "10px 14px", marginBottom: 16, fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <span>{dataError}</span>
            <button style={{ ...styles.btnSmall, borderColor: "#FFD98A", color: "#B5720A" }} onClick={() => setDataError("")}>Stäng</button>
          </div>
        )}

        {tab === "plattform" && (
          <>
            <h1 style={styles.h1}>Plattform</h1>
            <p style={styles.sub}>Översikt över alla bilvårdsföretag som abonnerar på tjänsten</p>
            <div style={styles.cardRow}>
              <div style={styles.statCard}><div style={styles.statNum}>{tenants.filter((t) => t.status === "active").reduce((sum, t) => sum + (PLAN_PRICES[t.plan] || 0), 0)} kr</div><div style={styles.statLabel}>MRR (månadsintäkt)</div></div>
              <div style={styles.statCard}><div style={styles.statNum}>{tenants.filter((t) => t.status === "active").length}</div><div style={styles.statLabel}>Aktiva kunder</div></div>
              <div style={styles.statCard}><div style={styles.statNum}>{tenants.filter((t) => t.status === "trial").length}</div><div style={styles.statLabel}>I provperiod</div></div>
              <div style={styles.statCard}><div style={styles.statNum}>{tenants.filter((t) => t.status === "past_due").length}</div><div style={styles.statLabel}>Betalning saknas</div></div>
            </div>

            <div style={styles.panel}>
              <div style={styles.panelTitle}>Lägg till kund</div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "end" }}>
                <div><label style={styles.label}>Företagsnamn</label><input style={styles.input} value={newTenant.name} onChange={(e) => setNewTenant({ ...newTenant, name: e.target.value })} placeholder="t.ex. Västkust Bilvård AB" /></div>
                <div>
                  <label style={styles.label}>Plan</label>
                  <select style={styles.input} value={newTenant.plan} onChange={(e) => setNewTenant({ ...newTenant, plan: e.target.value })}>
                    <option value="Basic">Basic ({PLAN_PRICES.Basic} kr/mån)</option>
                    <option value="Pro">Pro ({PLAN_PRICES.Pro} kr/mån)</option>
                  </select>
                </div>
                <div>
                  <label style={styles.label}>Status</label>
                  <select style={styles.input} value={newTenant.status} onChange={(e) => setNewTenant({ ...newTenant, status: e.target.value })}>
                    <option value="trial">Provperiod</option>
                    <option value="active">Aktiv</option>
                  </select>
                </div>
                <button style={styles.btn} onClick={addTenant}><Plus size={14} />Lägg till</button>
              </div>
            </div>

            <div style={styles.panel}>
              <div style={styles.panelTitle}>Kunder</div>
              {tenants.map((t) => (
                <div key={t.id} style={{ padding: "10px 0", borderBottom: "1px solid #E9EEF7" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                    <div>
                      <strong>{t.name}</strong>
                      <div style={{ fontSize: 12, color: "#6B7789", display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={styles.dot(TENANT_STATUS[t.status].dot)} />{TENANT_STATUS[t.status].label} · Kund sedan {t.signupDate}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <select style={styles.input} value={t.plan} onChange={(e) => setTenantPlan(t.id, e.target.value)}>
                        <option value="Basic">Basic ({PLAN_PRICES.Basic} kr/mån)</option>
                        <option value="Pro">Pro ({PLAN_PRICES.Pro} kr/mån)</option>
                      </select>
                      <select style={styles.input} value={t.status} onChange={(e) => setTenantStatus(t.id, e.target.value)}>
                        <option value="trial">Provperiod</option>
                        <option value="active">Aktiv</option>
                        <option value="past_due">Betalning saknas</option>
                        <option value="cancelled">Uppsagd</option>
                      </select>
                      <button style={styles.btnSmall} onClick={() => removeTenant(t.id)}><Trash2 size={12} /></button>
                    </div>
                  </div>
                </div>
              ))}
              {tenants.length === 0 && <p style={styles.sub}>Inga kunder inlagda.</p>}
            </div>
          </>
        )}

        {tab === "oversikt" && (
          <>
            <h1 style={styles.h1}>Översikt</h1>
            <p style={styles.sub}>Dagens läge på verkstaden, {new Date().toLocaleDateString("sv-SE")}</p>
            <div style={styles.cardRow}>
              <div style={styles.statCard}><div style={styles.statNum}>{todaysOrders.length}</div><div style={styles.statLabel}>Ordrar idag</div></div>
              <div style={styles.statCard}><div style={styles.statNum}>{todaysOrders.filter((o) => getOrderStatus(o) === "pagar").length}</div><div style={styles.statLabel}>Pågående just nu</div></div>
              <div style={styles.statCard}><div style={styles.statNum}>{todaysOrders.filter((o) => getOrderStatus(o) === "klar").length}</div><div style={styles.statLabel}>Klara idag</div></div>
              <div style={styles.statCard}><div style={styles.statNum}>{staff.filter((s) => clockState(s.id) === "arbetar" || clockState(s.id) === "rast").length}</div><div style={styles.statLabel}>Instämplade nu</div></div>
            </div>
            <div style={styles.panel}>
              <div style={styles.panelTitle}>Dagens jobb</div>
              {todaysOrders.map((o) => (
                <div key={o.id} style={styles.row}>
                  <div>
                    <strong>{o.tid}</strong> — {o.kund} · {o.bil} · {o.tjanst}
                    <div style={{ fontSize: 12, color: "#6B7789" }}>{staffName(o.staffId)}</div>
                  </div>
                  <StatusPill status={getOrderStatus(o)} />
                </div>
              ))}
              {todaysOrders.length === 0 && <p style={styles.sub}>Inga ordrar inbokade idag.</p>}
            </div>
          </>
        )}

        {tab === "nyheter" && (
          <>
            <h1 style={styles.h1}>Nyheter</h1>
            <p style={styles.sub}>Information från ledningen till hela verksamheten</p>

            {canManageStaff && (
              <div style={styles.panel}>
                <div style={styles.panelTitle}>Publicera nyhet</div>
                <div style={{ marginBottom: 10 }}>
                  <label style={styles.label}>Rubrik</label>
                  <input style={{ ...styles.input, width: "100%" }} value={newAnnouncement.title} onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })} placeholder="t.ex. Nya rutiner" />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={styles.label}>Text</label>
                  <textarea style={{ ...styles.input, width: "100%", minHeight: 70, fontFamily: "inherit" }} value={newAnnouncement.text} onChange={(e) => setNewAnnouncement({ ...newAnnouncement, text: e.target.value })} placeholder="Skriv meddelandet till personalen..." />
                </div>
                <button style={styles.btn} onClick={publishAnnouncement}><Megaphone size={14} />Publicera</button>
              </div>
            )}

            {announcements.map((a) => (
              <div key={a.id} style={styles.reportCard}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  {!a.read && <span style={styles.dot("#FF8A34")} />}
                  <span style={styles.reportName}>{a.title}</span>
                </div>
                <div style={{ fontSize: 12, color: "#6B7789", marginBottom: 8 }}>{a.author} · {a.at}</div>
                <p style={{ fontSize: 13, color: "#475467", margin: 0 }}>{a.text}</p>
              </div>
            ))}
            {announcements.length === 0 && <p style={styles.sub}>Inga nyheter publicerade än.</p>}
          </>
        )}

        {tab === "personal" && (
          <>
            <h1 style={styles.h1}>Personal</h1>
            <p style={styles.sub}>Personalstyrka och timlöner</p>
            <div style={styles.panel}>
              {staff.map((s) => {
                const form = coveringForm[s.id] || { reason: "semester", from: today(), to: today() };
                return (
                  <div key={s.id} style={{ padding: "10px 0", borderBottom: "1px solid #E9EEF7" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <strong>{s.name}</strong><div style={{ fontSize: 12, color: "#6B7789" }}>{s.roll}</div>
                        {s.covering && (
                          <div style={{ ...styles.stepStatus("pagar"), display: "inline-flex", marginTop: 4 }}>
                            Vikarierande arbetsledare · {s.covering.reason === "semester" ? "Semester" : s.covering.reason === "sjuk" ? "Sjuk" : "Annat"} · {s.covering.from}–{s.covering.to}
                          </div>
                        )}
                      </div>
                      {canManageStaff && <div>{s.rate} kr/h</div>}
                    </div>
                    {canManageStaff && (
                      <div style={{ marginTop: 8 }}>
                        {s.covering ? (
                          <button style={styles.btnSmall} onClick={() => clearCovering(s.id)}>Ta bort vikariat</button>
                        ) : (
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "end" }}>
                            <div>
                              <label style={styles.label}>Orsak</label>
                              <select style={styles.input} value={form.reason} onChange={(e) => setCoveringForm({ ...coveringForm, [s.id]: { ...form, reason: e.target.value } })}>
                                <option value="semester">Semester</option>
                                <option value="sjuk">Sjuk</option>
                                <option value="annat">Annat</option>
                              </select>
                            </div>
                            <div><label style={styles.label}>Från</label><input type="date" style={styles.input} value={form.from} onChange={(e) => setCoveringForm({ ...coveringForm, [s.id]: { ...form, from: e.target.value } })} /></div>
                            <div><label style={styles.label}>Till</label><input type="date" style={styles.input} value={form.to} onChange={(e) => setCoveringForm({ ...coveringForm, [s.id]: { ...form, to: e.target.value } })} /></div>
                            <button style={styles.btnGhost} onClick={() => setCovering(s.id, form)}>Gör till vikarierande arbetsledare</button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <p style={{ ...styles.sub, marginTop: -8 }}>Används när ordinarie arbetsledare har semester eller är sjuk — markera vem som täcker upp och för vilken period.</p>
            {canManageStaff ? (
              <div style={styles.panel}>
                <div style={styles.panelTitle}>Lägg till personal</div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "end" }}>
                  <div><label style={styles.label}>Namn</label><input style={styles.input} value={newStaff.name} onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })} placeholder="Förnamn Efternamn" /></div>
                  <div><label style={styles.label}>Roll</label><input style={styles.input} value={newStaff.roll} onChange={(e) => setNewStaff({ ...newStaff, roll: e.target.value })} placeholder="Detailer" /></div>
                  <div><label style={styles.label}>Timlön (kr)</label><input style={styles.input} type="number" value={newStaff.rate} onChange={(e) => setNewStaff({ ...newStaff, rate: e.target.value })} placeholder="185" /></div>
                  <button style={styles.btn} onClick={addStaff}><Plus size={14} />Lägg till</button>
                </div>
              </div>
            ) : (
              <p style={styles.sub}>Endast platschef och ägare kan lägga till eller ta bort personal.</p>
            )}

            {canManageStaff && (
              <div style={styles.panel}>
                <div style={styles.panelTitle}>Bjud in personal (konto)</div>
                <p style={{ fontSize: 12, color: "#6B7789", margin: "0 0 14px" }}>Skickar en inbjudningslänk till personen så de kan skapa sitt eget lösenord. Du får en länk att skicka själv — inget mejl skickas härifrån. Personen väljer sitt eget lösenord.</p>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "end", marginBottom: 16 }}>
                  <div><label style={styles.label}>Namn</label><input style={styles.input} value={inviteForm.name} onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })} placeholder="Förnamn Efternamn" /></div>
                  <div><label style={styles.label}>E-post</label><input style={styles.input} type="email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} placeholder="namn@epost.se" /></div>
                  <div>
                    <label style={styles.label}>Roll</label>
                    <select style={styles.input} value={inviteForm.role} onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}>
                      <option value="tekniker">Personal</option>
                      <option value="arbetsledare">Arbetsledare</option>
                      <option value="platschef">Platschef</option>
                      <option value="agare">Admin</option>
                    </select>
                  </div>
                  <button style={styles.btn} onClick={sendInvite}><Plus size={14} />Skicka inbjudan</button>
                </div>
                {pendingInvites.map((inv) => (
                  <div key={inv.id} style={styles.row}>
                    <div>
                      <strong>{inv.name}</strong> · {inv.email}
                      <div style={{ fontSize: 12, color: "#6B7789" }}>{inv.role === "tekniker" ? "Personal" : inv.role === "arbetsledare" ? "Arbetsledare" : inv.role === "platschef" ? "Platschef" : "Admin"} · {inv.at}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 12, color: "#B5720A" }}>{inv.status}</span>
                      <button style={styles.btnSmall} onClick={() => cancelInvite(inv.id)}><Trash2 size={12} /></button>
                    </div>
                  </div>
                ))}
                {pendingInvites.length === 0 && <p style={{ ...styles.sub, margin: 0 }}>Inga väntande inbjudningar.</p>}
              </div>
            )}
          </>
        )}

        {tab === "schema" && (
          <>
            <h1 style={styles.h1}>Schema</h1>
            <p style={styles.sub}>Välj person, dag och station — det syns direkt på personens rad</p>

            <div style={styles.panel}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4, flexWrap: "wrap", gap: 10 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <button style={styles.btnGhost} onClick={() => setWeekAnchor(addDays(weekAnchor, -7))}>‹ Förra veckan</button>
                  <button style={styles.btnGhost} onClick={() => setWeekAnchor(today())}>Denna vecka</button>
                  <button style={styles.btnGhost} onClick={() => setWeekAnchor(addDays(weekAnchor, 7))}>Nästa vecka ›</button>
                </div>
                {canManageWork && (
                  <button style={weekPublished ? styles.btnGhost : styles.btn} onClick={weekPublished ? unpublishWeek : publishWeek}>
                    {weekPublished ? "Avpublicera" : "Publicera schema"}
                  </button>
                )}
              </div>
              <p style={{ fontSize: 12, color: "#6B7789", margin: "6px 0 4px" }}>Vecka: {dayLabel(weekDates[0])} – {dayLabel(weekDates[6])}</p>
              <p style={{ fontSize: 12, color: weekPublished ? "#1657C8" : "#6B7789", margin: "0 0 16px" }}>
                {weekPublished ? `Publicerad ${weekPublishedAt} — synlig för all personal` : "Utkast — inte publicerad för personalen än"}
              </p>

              {canManageWork && (
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "end", marginBottom: 20, paddingBottom: 20, borderBottom: "1px solid #EEF1F7" }}>
                  <div>
                    <label style={styles.label}>Personal</label>
                    <select style={styles.input} value={assignForm.staffId} onChange={(e) => setAssignForm({ ...assignForm, staffId: e.target.value })}>
                      {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={styles.label}>Dag</label>
                    <select style={styles.input} value={assignForm.date} onChange={(e) => setAssignForm({ ...assignForm, date: e.target.value })}>
                      {weekDates.map((d) => <option key={d} value={d}>{dayLabel(d)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={styles.label}>Typ</label>
                    <select style={styles.input} value={assignForm.kind} onChange={(e) => setAssignForm({ ...assignForm, kind: e.target.value })}>
                      <option value="station">Station</option>
                      <option value="rast">Rast</option>
                      <option value="fritext">Fritext</option>
                    </select>
                  </div>
                  {assignForm.kind === "station" && (
                    <div>
                      <label style={styles.label}>Station</label>
                      <select style={styles.input} value={assignForm.station} onChange={(e) => setAssignForm({ ...assignForm, station: e.target.value })}>
                        {stepLabels.map((n) => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </div>
                  )}
                  {assignForm.kind === "fritext" && (
                    <div>
                      <label style={styles.label}>Vad</label>
                      <input style={styles.input} value={assignForm.freeText} onChange={(e) => setAssignForm({ ...assignForm, freeText: e.target.value })} placeholder="t.ex. Städning, Körning, Hämtning" />
                    </div>
                  )}
                  <div><label style={styles.label}>Start</label><input type="time" style={styles.input} value={assignForm.start} onChange={(e) => setAssignForm({ ...assignForm, start: e.target.value })} /></div>
                  <div><label style={styles.label}>Slut</label><input type="time" style={styles.input} value={assignForm.end} onChange={(e) => setAssignForm({ ...assignForm, end: e.target.value })} /></div>
                  <button style={styles.btn} onClick={submitAssignment}><Plus size={14} />Lägg till</button>
                </div>
              )}

              {canManageWork || weekPublished ? (
                staff.map((s) => (
                  <div key={s.id} style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{s.name}</div>
                    {weekDates.map((d) => {
                      const entries = getAssignments(d, s.id).slice().sort((a, b) => (a.start || "").localeCompare(b.start || ""));
                      if (entries.length === 0) return null;
                      return (
                        <div key={d} style={{ padding: "8px 0", borderBottom: "1px solid #EEF1F7" }}>
                          <div style={{ fontSize: 13, marginBottom: 6 }}>{dayLabel(d)}</div>
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {entries.map((e) => (
                              <span
                                key={e.id}
                                style={
                                  e.kind === "rast"
                                    ? { ...styles.stationChip, background: "#FFF3D6", borderColor: "#FFD98A", color: "#8A6A00" }
                                    : e.kind === "fritext"
                                    ? { ...styles.stationChip, background: "#FFE9D6", borderColor: "#FFC79A", color: "#C1560A" }
                                    : { ...styles.stationChip, background: "#E8F0FE", borderColor: "#9FC7FB", color: "#1657C8" }
                                }
                              >
                                {e.start && e.end ? `${e.start}–${e.end} ` : ""}{e.kind === "rast" ? "Rast" : e.station}
                                {canManageWork && <button style={styles.stationChipX} onClick={() => removeEntryForDay(d, s.id, e.id)}>×</button>}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                    {weekDates.every((d) => getAssignments(d, s.id).length === 0) && <p style={{ ...styles.sub, margin: 0 }}>Inga pass inplanerade denna vecka.</p>}
                  </div>
                ))
              ) : (
                <p style={styles.sub}>Schemat är inte publicerat än.</p>
              )}
            </div>
          </>
        )}

        {tab === "tidrapport" && (
          <>
            <h1 style={styles.h1}>Stämpelklocka</h1>
            <p style={styles.sub}>Tillfälligt avstängd</p>

            <div style={{ ...styles.panel, textAlign: "center", padding: "48px 32px" }}>
              <Timer size={32} style={{ color: "#94A3B8", marginBottom: 14 }} />
              <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 20, fontWeight: 600, color: "#1F2A44", marginBottom: 8 }}>
                Väntar på godkännande från Skatteverket
              </div>
              <p style={{ fontSize: 13, color: "#6B7789", maxWidth: 460, margin: "0 auto", lineHeight: 1.6 }}>
                Stämpelklockan är avstängd tills den elektroniska personalliggaren
                är godkänd. Registrera tider på papper under tiden.
              </p>
            </div>
          </>
        )}

        {tab === "mitt-konto" && (
          <>
            <h1 style={styles.h1}>Mitt konto</h1>
            <p style={styles.sub}>{currentUser?.name} · {currentUser?.email}</p>

            <div style={{ ...styles.panel, maxWidth: 420 }}>
              <div style={styles.panelTitle}>Byt lösenord</div>
              <p style={{ fontSize: 12, color: "#6B7789", margin: "0 0 14px", lineHeight: 1.5 }}>
                När du byter loggas alla dina andra inloggningar ut — på andra
                datorer och telefoner. Den här sessionen påverkas inte.
              </p>
              {renderLosenordsformular("Byt lösenord")}
            </div>
          </>
        )}

        {tab === "ordrar" && (
          <>
            <h1 style={styles.h1}>Ordrar</h1>
            <p style={styles.sub}>Alla bokade och pågående uppdrag</p>
            <div style={styles.panel}>
              {orders.length === 0 && <p style={styles.sub}>Inga ordrar upplagda än.</p>}
              {orders.map((o) => (
                redigerarOrder?.id === o.id ? (
                  <div key={o.id} style={{ ...styles.row, display: "block", background: "#EEF2FA", padding: "14px 12px" }}>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "end" }}>
                      <div><label style={styles.label}>Kund</label><input style={styles.input} value={redigerarOrder.kund} onChange={(e) => setRedigerarOrder({ ...redigerarOrder, kund: e.target.value })} /></div>
                      <div><label style={styles.label}>Bil</label><input style={styles.input} value={redigerarOrder.bil} onChange={(e) => setRedigerarOrder({ ...redigerarOrder, bil: e.target.value })} /></div>
                      <div><label style={styles.label}>Tjänst</label><input style={styles.input} value={redigerarOrder.tjanst} onChange={(e) => setRedigerarOrder({ ...redigerarOrder, tjanst: e.target.value })} /></div>
                      <div>
                        <label style={styles.label}>Personal</label>
                        <select style={styles.input} value={redigerarOrder.staffId} onChange={(e) => setRedigerarOrder({ ...redigerarOrder, staffId: e.target.value })}>
                          <option value="">Otilldelad</option>
                          {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      </div>
                      <div><label style={styles.label}>Datum</label><input style={styles.input} type="date" value={redigerarOrder.datum} onChange={(e) => setRedigerarOrder({ ...redigerarOrder, datum: e.target.value })} /></div>
                      <div><label style={styles.label}>Tid</label><input style={styles.input} type="time" value={redigerarOrder.tid} onChange={(e) => setRedigerarOrder({ ...redigerarOrder, tid: e.target.value })} /></div>
                      <div><label style={styles.label}>Timmar</label><input style={{ ...styles.input, width: 60 }} type="number" min="1" value={redigerarOrder.timmar} onChange={(e) => setRedigerarOrder({ ...redigerarOrder, timmar: e.target.value })} /></div>
                      <button style={styles.btn} onClick={sparaRedigering}><Check size={14} />Spara</button>
                      <button style={styles.btnGhost} onClick={() => setRedigerarOrder(null)}>Avbryt</button>
                    </div>
                    <p style={{ fontSize: 11, color: "#6B7789", margin: "10px 0 0" }}>
                      Momenten och den loggade tiden påverkas inte — de hör till arbetet som utfördes.
                    </p>
                  </div>
                ) : (
                  <div key={o.id} style={styles.row}>
                    <div>
                      <strong>{o.kund}</strong> · {o.bil} · {o.tjanst}
                      <div style={{ fontSize: 12, color: "#6B7789" }}>{o.datum} {o.tid} · {staffName(o.staffId)} · {o.timmar}h</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <StatusPill status={getOrderStatus(o)} />
                      {canManageWork && (
                        <>
                          <button style={styles.btnSmall} onClick={() => startaRedigering(o)}>Ändra</button>
                          <button style={styles.btnSmall} onClick={() => taBortOrder(o)}><Trash2 size={12} /></button>
                        </>
                      )}
                    </div>
                  </div>
                )
              ))}
            </div>
            {canManageWork ? (
              <div style={styles.panel}>
                <div style={styles.panelTitle}>Ny order</div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "end" }}>
                  <div><label style={styles.label}>Kund</label><input style={styles.input} value={newOrder.kund} onChange={(e) => setNewOrder({ ...newOrder, kund: e.target.value })} placeholder="Kundnamn" /></div>
                  <div><label style={styles.label}>Bil</label><input style={styles.input} value={newOrder.bil} onChange={(e) => setNewOrder({ ...newOrder, bil: e.target.value })} placeholder="Modell" /></div>
                  <div><label style={styles.label}>Tjänst</label><input style={styles.input} value={newOrder.tjanst} onChange={(e) => setNewOrder({ ...newOrder, tjanst: e.target.value })} placeholder="Helglans" /></div>
                  <div>
                    <label style={styles.label}>Personal</label>
                    <select style={styles.input} value={newOrder.staffId} onChange={(e) => setNewOrder({ ...newOrder, staffId: e.target.value })}>
                      {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                  <div><label style={styles.label}>Datum</label><input style={styles.input} type="date" value={newOrder.datum} onChange={(e) => setNewOrder({ ...newOrder, datum: e.target.value })} /></div>
                  <div><label style={styles.label}>Tid</label><input style={styles.input} type="time" value={newOrder.tid} onChange={(e) => setNewOrder({ ...newOrder, tid: e.target.value })} /></div>
                  <div><label style={styles.label}>Timmar</label><input style={{ ...styles.input, width: 60 }} type="number" min="0.5" step="0.5" value={newOrder.timmar} onChange={(e) => setNewOrder({ ...newOrder, timmar: e.target.value })} /></div>
                  <button style={styles.btn} onClick={addOrder}><Plus size={14} />Skapa order</button>
                </div>
              </div>
            ) : (
              <p style={styles.sub}>Endast arbetsledare, platschef och ägare kan skapa nya ordrar.</p>
            )}
          </>
        )}

        {tab === "bokningar" && (
          <>
            <h1 style={styles.h1}>Bokningar</h1>
            <p style={styles.sub}>Inkommande bokningar från företagskunder</p>

            {canManageWork && (
              <div style={styles.panel}>
                <div style={styles.panelTitle}>Bjud in företagskund</div>
                <p style={{ fontSize: 12, color: "#6B7789", margin: "0 0 14px" }}>Kunden får en länk för att sätta eget lösenord och kommer direkt till sina egna bokningar. Du får en länk att skicka själv — inget mejl skickas härifrån. Personen väljer sitt eget lösenord.</p>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "end", marginBottom: 16 }}>
                  <div><label style={styles.label}>Företagsnamn</label><input style={styles.input} value={customerInviteForm.company} onChange={(e) => setCustomerInviteForm({ ...customerInviteForm, company: e.target.value })} placeholder="t.ex. Bilcenter Kristianstad AB" /></div>
                  <div><label style={styles.label}>Kontaktperson</label><input style={styles.input} value={customerInviteForm.contact} onChange={(e) => setCustomerInviteForm({ ...customerInviteForm, contact: e.target.value })} /></div>
                  <div><label style={styles.label}>E-post</label><input style={styles.input} type="email" value={customerInviteForm.email} onChange={(e) => setCustomerInviteForm({ ...customerInviteForm, email: e.target.value })} placeholder="namn@epost.se" /></div>
                  <button style={styles.btn} onClick={sendCustomerInvite}><Plus size={14} />Skicka inbjudan</button>
                </div>
                {pendingCustomerInvites.map((inv) => (
                  <div key={inv.id} style={styles.row}>
                    <div>
                      <strong>{inv.company}</strong> · {inv.email}
                      <div style={{ fontSize: 12, color: "#6B7789" }}>{inv.contact ? inv.contact + " · " : ""}{inv.at}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 12, color: "#B5720A" }}>{inv.status}</span>
                      <button style={styles.btnSmall} onClick={() => cancelCustomerInvite(inv.id)}><Trash2 size={12} /></button>
                    </div>
                  </div>
                ))}
                {pendingCustomerInvites.length === 0 && <p style={{ ...styles.sub, margin: 0 }}>Inga väntande inbjudningar.</p>}
              </div>
            )}

            {orders.filter((o) => o.company).length === 0 && <p style={styles.sub}>Inga bokningar från företag än.</p>}
            {orders.filter((o) => o.company).map((o) => {
              const unreadForThis = (o.chat || []).filter((m) => !m.readByStaff).length;
              return (
              <div key={o.id} style={styles.panel}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                      {(o.bookingStatus === "ny" || unreadForThis > 0) && <span style={styles.dot("#FF8A34")} />}
                      {o.company}
                    </div>
                    <div style={{ fontSize: 12, color: "#6B7789" }}>{o.bil}{o.tjanst ? " · " + o.tjanst : ""}</div>
                    <div style={{ fontSize: 12, color: "#6B7789", marginTop: 6 }}>
                      Hämtning: {o.hamtning?.mode === "normal" ? "Snarast/normalt" : `${o.hamtning?.datum || "?"} ${o.hamtning?.tid || ""}`}<br />
                      Önskad återlämning: {o.aterlamning?.mode === "normal" ? "Snarast/normalt" : `${o.aterlamning?.datum || "?"} ${o.aterlamning?.tid || ""}`}
                    </div>
                    {(o.steps.length > 0 || o.extraSteps.length > 0) && (
                      <div style={{ fontSize: 12, color: "#1657C8", marginTop: 6, fontWeight: 600 }}>
                        Tjänster: {[...o.steps, ...o.extraSteps].map((s) => s.name).join(", ")}
                      </div>
                    )}
                    {o.notes && <p style={{ fontSize: 12, color: "#475467", marginTop: 6 }}>{o.notes}</p>}
                  </div>
                  <BookingStatusPill status={o.bookingStatus} />
                </div>
                {canManageWork && (
                  <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                    {o.bookingStatus === "ny" && <button style={styles.btn} onClick={() => advanceBooking(o.id, "mottagen")}>Markera mottagen</button>}
                    {o.bookingStatus === "mottagen" && <button style={styles.btn} onClick={() => advanceBooking(o.id, "pagar")}>Starta — Pågår</button>}
                    {o.bookingStatus === "pagar" && <button style={styles.btn} onClick={() => advanceBooking(o.id, "klar")}>Markera klar</button>}
                    <button style={styles.btnGhost} onClick={() => startaRedigering(o)}>Ändra</button>
                    <button style={styles.btnSmall} onClick={() => taBortOrder(o)}><Trash2 size={12} /> Ta bort</button>
                  </div>
                )}
                {canManageWork && redigerarOrder?.id === o.id && (
                  <div style={{ background: "#EEF2FA", padding: "14px 12px", marginTop: 10 }}>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "end" }}>
                      <div><label style={styles.label}>Kund</label><input style={styles.input} value={redigerarOrder.kund} onChange={(e) => setRedigerarOrder({ ...redigerarOrder, kund: e.target.value })} /></div>
                      <div><label style={styles.label}>Bil</label><input style={styles.input} value={redigerarOrder.bil} onChange={(e) => setRedigerarOrder({ ...redigerarOrder, bil: e.target.value })} /></div>
                      <div><label style={styles.label}>Tjänst</label><input style={styles.input} value={redigerarOrder.tjanst} onChange={(e) => setRedigerarOrder({ ...redigerarOrder, tjanst: e.target.value })} /></div>
                      <div>
                        <label style={styles.label}>Personal</label>
                        <select style={styles.input} value={redigerarOrder.staffId} onChange={(e) => setRedigerarOrder({ ...redigerarOrder, staffId: e.target.value })}>
                          <option value="">Otilldelad</option>
                          {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      </div>
                      <div><label style={styles.label}>Datum</label><input style={styles.input} type="date" value={redigerarOrder.datum} onChange={(e) => setRedigerarOrder({ ...redigerarOrder, datum: e.target.value })} /></div>
                      <div><label style={styles.label}>Tid</label><input style={styles.input} type="time" value={redigerarOrder.tid} onChange={(e) => setRedigerarOrder({ ...redigerarOrder, tid: e.target.value })} /></div>
                      <button style={styles.btn} onClick={sparaRedigering}><Check size={14} />Spara</button>
                      <button style={styles.btnGhost} onClick={() => setRedigerarOrder(null)}>Avbryt</button>
                    </div>
                  </div>
                )}
                {o.bookingStatus === "klar" && (
                  <div style={{ fontSize: 11, marginTop: 10, display: "flex", alignItems: "center", gap: 5, color: o.kvittens ? "#1657C8" : "#94A3B8" }}>
                    <ThumbsUp size={13} />
                    {o.kvittens
                      ? o.kvittens.auto
                        ? `Kvitterad automatiskt ${new Date(o.kvittens.at).toLocaleString("sv-SE")}`
                        : `Kvitterad av ${o.kvittens.by} ${new Date(o.kvittens.at).toLocaleString("sv-SE")}`
                      : `Väntar på kundens bekräftelse — kvitteras automatiskt om ${timmarTillAutoKvittens(o)} h`}
                  </div>
                )}
                {renderChat(o, shopName, unreadForThis)}
              </div>
              );
            })}
          </>
        )}

        {tab === "kundkontakt" && (
          <>
            <h1 style={styles.h1}>Kundkontakt</h1>
            <p style={styles.sub}>Skicka meddelanden till era företagskunder och boka möten med röst och kamera</p>

            <div style={styles.panel}>
              <div style={styles.panelTitle}>Skicka meddelande</div>
              <div style={{ marginBottom: 10 }}>
                <label style={styles.label}>Till</label>
                <select style={styles.input} value={newCustomerMessage.target} onChange={(e) => setNewCustomerMessage({ ...newCustomerMessage, target: e.target.value })}>
                  <option value="alla">Alla kunder</option>
                  {registeredCustomers.filter((c) => c.status === "active").map((c) => <option key={c.id} value={c.company}>{c.company}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={styles.label}>Rubrik</label>
                <input style={{ ...styles.input, width: "100%" }} value={newCustomerMessage.title} onChange={(e) => setNewCustomerMessage({ ...newCustomerMessage, title: e.target.value })} placeholder="t.ex. Ändrade öppettider vecka 42" />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={styles.label}>Text</label>
                <textarea style={{ ...styles.input, width: "100%", minHeight: 70, fontFamily: "inherit" }} value={newCustomerMessage.text} onChange={(e) => setNewCustomerMessage({ ...newCustomerMessage, text: e.target.value })} />
              </div>
              <button style={styles.btn} onClick={sendCustomerMessage}><Radio size={14} />Skicka</button>
            </div>

            <div style={styles.panel}>
              <div style={styles.panelTitle}>Skickade meddelanden</div>
              {customerMessages.map((m) => (
                <div key={m.id} style={{ padding: "8px 0", borderBottom: "1px solid #E9EEF7" }}>
                  <strong>{m.title}</strong>
                  <div style={{ fontSize: 12, color: "#6B7789" }}>Till: {m.target === "alla" ? "Alla kunder" : m.target} · {m.at}</div>
                  <p style={{ fontSize: 13, color: "#475467", margin: "4px 0 0" }}>{m.text}</p>
                </div>
              ))}
              {customerMessages.length === 0 && <p style={styles.sub}>Inga meddelanden skickade än.</p>}
            </div>

            <div style={styles.panel}>
              <div style={styles.panelTitle}>Boka möte (röst & kamera)</div>
              <p style={{ fontSize: 12, color: "#6B7789", margin: "0 0 14px" }}>Skapar ett videomöterum där flera kan delta samtidigt — bra för uppföljning eller månadssammanställning.</p>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "end", marginBottom: 16 }}>
                <div>
                  <label style={styles.label}>Till</label>
                  <select style={styles.input} value={newMeeting.target} onChange={(e) => setNewMeeting({ ...newMeeting, target: e.target.value })}>
                    <option value="alla">Alla kunder</option>
                    {registeredCustomers.filter((c) => c.status === "active").map((c) => <option key={c.id} value={c.company}>{c.company}</option>)}
                  </select>
                </div>
                <div>
                  <label style={styles.label}>Typ</label>
                  <select style={styles.input} value={newMeeting.type} onChange={(e) => setNewMeeting({ ...newMeeting, type: e.target.value })}>
                    <option value="Uppföljning">Uppföljning</option>
                    <option value="Månadssammanställning">Månadssammanställning</option>
                    <option value="Annat">Annat</option>
                  </select>
                </div>
                <div><label style={styles.label}>Titel</label><input style={styles.input} value={newMeeting.title} onChange={(e) => setNewMeeting({ ...newMeeting, title: e.target.value })} placeholder="t.ex. September-avstämning" /></div>
                <div><label style={styles.label}>Datum</label><input type="date" style={styles.input} value={newMeeting.datum} onChange={(e) => setNewMeeting({ ...newMeeting, datum: e.target.value })} /></div>
                <div><label style={styles.label}>Tid</label><input type="time" style={styles.input} value={newMeeting.tid} onChange={(e) => setNewMeeting({ ...newMeeting, tid: e.target.value })} /></div>
                <button style={styles.btn} onClick={scheduleMeeting}><Video size={14} />Boka möte</button>
              </div>
              {meetings.map((m) => (
                <div key={m.id} style={styles.row}>
                  <div>
                    <strong>{m.title}</strong>
                    <div style={{ fontSize: 12, color: "#6B7789" }}>{m.type} · Till: {m.target === "alla" ? "Alla kunder" : m.target} · {m.datum} {m.tid}</div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <a href={`https://meet.jit.si/${m.roomName}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                      <button style={styles.btn}><Video size={13} />Gå med i mötet</button>
                    </a>
                    <button style={styles.btnSmall} onClick={() => removeMeeting(m.id)}><Trash2 size={12} /></button>
                  </div>
                </div>
              ))}
              {meetings.length === 0 && <p style={styles.sub}>Inga möten bokade än.</p>}
              <p style={{ fontSize: 11, color: "#94A3B8", marginTop: 10 }}>Videomötena körs via Jitsi Meet (gratis, ingen inloggning krävs) — fungerar med flera deltagare, röst och kamera direkt i webbläsaren.</p>
            </div>
          </>
        )}

        {tab === "mina-bokningar" && (
          <>
            <h1 style={styles.h1}>Mina bokningar</h1>
            <p style={styles.sub}>Bokningar för {companyName}</p>
            {orders.filter((o) => o.company === companyName).length === 0 && <p style={styles.sub}>Inga bokningar än — skapa en under "Ny bokning".</p>}
            {orders.filter((o) => o.company === companyName).map((o) => {
              const unreadForThis = (o.chat || []).filter((m) => !m.readByCustomer).length;
              return (
              <div key={o.id} style={styles.panel}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                      {unreadForThis > 0 && <span style={styles.dot("#FF8A34")} />}
                      {o.bil}
                    </div>
                    {o.tjanst && <div style={{ fontSize: 12, color: "#6B7789" }}>{o.tjanst}</div>}
                    <div style={{ fontSize: 12, color: "#6B7789", marginTop: 6 }}>
                      Hämtning: {o.hamtning?.mode === "normal" ? "Snarast/normalt" : `${o.hamtning?.datum || "?"} ${o.hamtning?.tid || ""}`}<br />
                      Önskad återlämning: {o.aterlamning?.mode === "normal" ? "Snarast/normalt" : `${o.aterlamning?.datum || "?"} ${o.aterlamning?.tid || ""}`}
                    </div>
                    {(o.steps.length > 0 || o.extraSteps.length > 0) && (
                      <div style={{ fontSize: 12, color: "#1657C8", marginTop: 6, fontWeight: 600 }}>
                        Tjänster: {[...o.steps, ...o.extraSteps].map((s) => s.name).filter((n) => n !== "Avslut" && n !== "Slutlig kontroll").join(", ")}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
                    <BookingStatusPill status={o.bookingStatus} />
                    {o.kvittens ? (
                      <div style={{ fontSize: 11, color: "#1657C8", display: "flex", alignItems: "center", gap: 5, textAlign: "right" }}>
                        <ThumbsUp size={13} />
                        {o.kvittens.auto
                          ? "Kvitterad automatiskt"
                          : `Kvitterad av ${o.kvittens.by}`}
                        <br />
                      </div>
                    ) : o.bookingStatus === "klar" ? (
                      <>
                        <button
                          style={{ ...styles.btn, background: "#1657C8" }}
                          onClick={() => kvitteraOrder(o.id)}
                        >
                          <ThumbsUp size={14} /> Bekräfta mottagen
                        </button>
                        <span style={{ fontSize: 10, color: "#94A3B8", textAlign: "right", maxWidth: 170 }}>
                          Kvitteras automatiskt om {timmarTillAutoKvittens(o)} h
                        </span>
                      </>
                    ) : (
                      // Knappen visas hela tiden, men avstängd — så att kunden
                      // vet att steget finns och inte undrar var det tog vägen.
                      <>
                        <button style={{ ...styles.btn, background: "#C7D6F0", cursor: "not-allowed" }} disabled>
                          <ThumbsUp size={14} /> Bekräfta mottagen
                        </button>
                        <span style={{ fontSize: 10, color: "#94A3B8", textAlign: "right", maxWidth: 170 }}>
                          Aktiveras när bilen är klar
                        </span>
                      </>
                    )}
                  </div>
                </div>
                {kundFarAndra(o) && kundRedigerar?.id !== o.id && (
                  <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                    <button style={styles.btnGhost} onClick={() => setKundRedigerar({
                      id: o.id, bil: o.bil, tjanst: o.tjanst ?? "",
                      hamtningMode: o.hamtning?.mode ?? "normal",
                      hamtningDatum: o.hamtning?.datum ?? "",
                      hamtningTid: o.hamtning?.tid ?? "",
                      aterlamningMode: o.aterlamning?.mode ?? "normal",
                      aterlamningDatum: o.aterlamning?.datum ?? "",
                      aterlamningTid: o.aterlamning?.tid ?? "",
                    })}>Ändra bokning</button>
                    <button style={styles.btnSmall} onClick={() => avbokaSomKund(o)}><Trash2 size={12} /> Avboka</button>
                  </div>
                )}

                {!kundFarAndra(o) && !o.kvittens && (
                  <p style={{ fontSize: 11, color: "#94A3B8", marginTop: 12 }}>
                    Arbetet har påbörjats — skriv ett meddelande nedan om något behöver ändras.
                  </p>
                )}

                {kundRedigerar?.id === o.id && (
                  <div style={{ background: "#EEF2FA", padding: "14px 12px", marginTop: 12 }}>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "end" }}>
                      <div><label style={styles.label}>Bil</label><input style={styles.input} value={kundRedigerar.bil} onChange={(e) => setKundRedigerar({ ...kundRedigerar, bil: e.target.value })} /></div>
                      <div><label style={styles.label}>Vad gäller det</label><input style={styles.input} value={kundRedigerar.tjanst} onChange={(e) => setKundRedigerar({ ...kundRedigerar, tjanst: e.target.value })} /></div>
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <label style={styles.label}>Hämtning</label>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                        <button type="button" style={kundRedigerar.hamtningMode === "normal" ? styles.btn : styles.btnGhost}
                          onClick={() => setKundRedigerar({ ...kundRedigerar, hamtningMode: "normal" })}>Normal / snarast</button>
                        <button type="button" style={kundRedigerar.hamtningMode === "datum" ? styles.btn : styles.btnGhost}
                          onClick={() => setKundRedigerar({ ...kundRedigerar, hamtningMode: "datum" })}>Ange datum & tid</button>
                        {kundRedigerar.hamtningMode === "datum" && (
                          <>
                            <input style={styles.input} type="date" value={kundRedigerar.hamtningDatum} onChange={(e) => setKundRedigerar({ ...kundRedigerar, hamtningDatum: e.target.value })} />
                            <input style={styles.input} type="time" value={kundRedigerar.hamtningTid} onChange={(e) => setKundRedigerar({ ...kundRedigerar, hamtningTid: e.target.value })} />
                          </>
                        )}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                      <button style={styles.btn} onClick={sparaKundandring}><Check size={14} />Spara</button>
                      <button style={styles.btnGhost} onClick={() => setKundRedigerar(null)}>Avbryt</button>
                    </div>
                  </div>
                )}

                {renderChat(o, companyName, unreadForThis, loggedInContact?.name ?? null)}
              </div>
              );
            })}
          </>
        )}

        {tab === "ny-bokning" && (
          <>
            <h1 style={styles.h1}>Ny bokning</h1>
            <p style={styles.sub}>Skicka en bokning till Glansgruppen</p>

            <div style={styles.panel}>
              <div style={{ marginBottom: 14 }}>
                <label style={styles.label}>Bil / registreringsnummer</label>
                <input style={{ ...styles.input, width: "100%" }} value={bookingForm.bil} onChange={(e) => setBookingForm({ ...bookingForm, bil: e.target.value })} placeholder="t.ex. Volvo V60, ABC123" />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={styles.label}>Paket</label>
                <button type="button" style={isRekondSelected ? styles.btn : styles.btnGhost} onClick={toggleRekondPackage}>Rekond</button>
                <p style={{ fontSize: 11, color: "#6B7789", margin: "6px 0 0" }}>I Rekond ingår: {rekondPackage.join(", ")}</p>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={styles.label}>Vilka tjänster vill ni ha? (bocka av det som gäller)</label>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                  {stepLabels.slice(0, 3).map((n) => (
                    <button key={n} type="button" style={styles.staffToggle(bookingForm.services.includes(n))} onClick={() => toggleBookingService(n)}>{n}</button>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: "#6B7789", margin: "4px 0" }}>Tillval</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {extraCatalog.map((n) => (
                    <button key={n} type="button" style={styles.staffToggle(bookingForm.services.includes(n))} onClick={() => toggleBookingService(n)}>{n}</button>
                  ))}
                </div>

                <div style={{ fontSize: 11, color: "#6B7789", margin: "12px 0 4px" }}>Något annat? Skriv fritt</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                  <input
                    style={{ ...styles.input, flex: 1, minWidth: 180 }}
                    value={customServiceDraft}
                    onChange={(e) => setCustomServiceDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomService(); } }}
                    placeholder="t.ex. Byta torkarblad"
                  />
                  <button type="button" style={styles.btnGhost} onClick={addCustomService}>
                    <Plus size={14} /> Lägg till
                  </button>
                </div>
                {customServices.length > 0 && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                    {customServices.map((n) => (
                      <span key={n} style={styles.stationChip}>
                        {n}
                        <button type="button" style={styles.stationChipX} onClick={() => toggleBookingService(n)}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={styles.label}>Vad gäller det (valfritt)</label>
                <input style={{ ...styles.input, width: "100%" }} value={bookingForm.info} onChange={(e) => setBookingForm({ ...bookingForm, info: e.target.value })} placeholder="t.ex. Helglans inför visning" />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={styles.label}>Vem bokar (bilsäljare)?</label>
                {loggedInContact ? (
                  <input style={{ ...styles.input, width: "100%" }} value={loggedInContact.name} disabled readOnly />
                ) : myCompanyAccount && myCompanyAccount.contacts && myCompanyAccount.contacts.length > 0 ? (
                  <select style={{ ...styles.input, width: "100%" }} value={bookingForm.kontakt} onChange={(e) => setBookingForm({ ...bookingForm, kontakt: e.target.value })}>
                    <option value="">Välj kontaktperson...</option>
                    {myCompanyAccount.contacts.map((ct) => <option key={ct.id} value={ct.name}>{ct.name}</option>)}
                  </select>
                ) : (
                  <input style={{ ...styles.input, width: "100%" }} value={bookingForm.kontakt} onChange={(e) => setBookingForm({ ...bookingForm, kontakt: e.target.value })} placeholder="Namn/telefon" />
                )}
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={styles.label}>Hämtning</label>
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <button type="button" style={bookingForm.hamtningMode === "normal" ? styles.btn : styles.btnGhost} onClick={() => setBookingForm({ ...bookingForm, hamtningMode: "normal" })}>Normal / snarast</button>
                  <button type="button" style={bookingForm.hamtningMode === "datum" ? styles.btn : styles.btnGhost} onClick={() => setBookingForm({ ...bookingForm, hamtningMode: "datum" })}>Ange datum & tid</button>
                </div>
                {bookingForm.hamtningMode === "datum" && (
                  <div style={{ display: "flex", gap: 8 }}>
                    <input type="date" style={styles.input} value={bookingForm.hamtningDatum} onChange={(e) => setBookingForm({ ...bookingForm, hamtningDatum: e.target.value })} />
                    <input type="time" style={styles.input} value={bookingForm.hamtningTid} onChange={(e) => setBookingForm({ ...bookingForm, hamtningTid: e.target.value })} />
                  </div>
                )}
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={styles.label}>Önskad återlämning</label>
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <button type="button" style={bookingForm.aterlamningMode === "normal" ? styles.btn : styles.btnGhost} onClick={() => setBookingForm({ ...bookingForm, aterlamningMode: "normal" })}>Normal</button>
                  <button type="button" style={bookingForm.aterlamningMode === "datum" ? styles.btn : styles.btnGhost} onClick={() => setBookingForm({ ...bookingForm, aterlamningMode: "datum" })}>Ange datum & tid</button>
                </div>
                {bookingForm.aterlamningMode === "datum" && (
                  <div style={{ display: "flex", gap: 8 }}>
                    <input type="date" style={styles.input} value={bookingForm.aterlamningDatum} onChange={(e) => setBookingForm({ ...bookingForm, aterlamningDatum: e.target.value })} />
                    <input type="time" style={styles.input} value={bookingForm.aterlamningTid} onChange={(e) => setBookingForm({ ...bookingForm, aterlamningTid: e.target.value })} />
                  </div>
                )}
              </div>

              <button style={styles.btn} onClick={submitBooking}><Plus size={14} />Skicka bokning</button>
            </div>
          </>
        )}

        {tab === "kundmeddelanden" && (
          <>
            <h1 style={styles.h1}>Meddelanden</h1>
            <p style={styles.sub}>Information från Glansgruppen</p>
            {customerMessages.filter((m) => m.target === "alla" || m.target === companyName).map((m) => (
              <div key={m.id} style={styles.reportCard}>
                <span style={styles.reportName}>{m.title}</span>
                <div style={{ fontSize: 12, color: "#6B7789", margin: "4px 0 8px" }}>{m.author} · {m.at}</div>
                <p style={{ fontSize: 13, color: "#475467", margin: 0 }}>{m.text}</p>
              </div>
            ))}
            {customerMessages.filter((m) => m.target === "alla" || m.target === companyName).length === 0 && <p style={styles.sub}>Inga meddelanden än.</p>}
          </>
        )}

        {tab === "kundmoten" && (
          <>
            <h1 style={styles.h1}>Möten</h1>
            <p style={styles.sub}>Videomöten med Glansgruppen — röst och kamera i webbläsaren</p>
            {meetings.filter((m) => m.target === "alla" || m.target === companyName).map((m) => (
              <div key={m.id} style={styles.panel}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                  <div>
                    <strong>{m.title}</strong>
                    <div style={{ fontSize: 12, color: "#6B7789" }}>{m.type} · {m.datum} {m.tid}</div>
                  </div>
                  <a href={`https://meet.jit.si/${m.roomName}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                    <button style={styles.btn}><Video size={13} />Gå med i mötet</button>
                  </a>
                </div>
              </div>
            ))}
            {meetings.filter((m) => m.target === "alla" || m.target === companyName).length === 0 && <p style={styles.sub}>Inga möten bokade än.</p>}
          </>
        )}

        {tab === "planering" && (
          <>
            <h1 style={styles.h1}>Live-planering</h1>
            <p style={styles.sub}>Klicka på ett arbetsmoment för att checka in det</p>
            <div style={styles.panel}>
              {todaysOrders.slice().sort((a, b) => a.tid.localeCompare(b.tid)).map((o) => (
                <div key={o.id} style={{ padding: "14px 0", borderBottom: "1px solid #EEF1F7" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>
                        <Clock size={13} style={{ verticalAlign: -2, marginRight: 6, color: "#6B7789" }} />
                        {o.datum} {o.tid}
                      </div>
                      <div style={{ fontSize: 13 }}>{o.kund} – {o.bil}</div>
                      {o.tjanst && <div style={{ fontSize: 12, color: "#6B7789" }}>{o.tjanst}</div>}
                      <div style={{ fontSize: 12, color: "#6B7789" }}>{staffName(o.staffId)}</div>
                    </div>
                    <StatusPill status={getOrderStatus(o)} />
                  </div>
                  <div>
                    {o.steps.map((s, i) => {
                      if (s.isFinal) {
                        return (
                          <div key={i} style={styles.stepRow}>
                            <span style={styles.stepChip(s.done)} onClick={() => toggleStep(o.id, i)}>
                              {s.done && <Check size={12} />}{i + 1}. {s.name}
                            </span>
                          </div>
                        );
                      }
                      const state = s.done ? "klar" : s.timerRunning ? "pagar" : "ej";
                      return (
                        <div key={i} style={{ marginBottom: 10 }}>
                          <div style={styles.stepRow}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <span style={{ fontSize: 13 }}>{i + 1}. {s.name}</span>
                              <span style={styles.stepStatus(state)}>{state === "klar" ? "Klar" : state === "pagar" ? "Pågår" : "Ej påbörjad"}</span>
                            </div>
                            <div style={styles.timerBox}>
                              {state === "pagar" || !canManageWork ? (
                                <span style={styles.timerText}>{state === "ej" ? "--:--" : formatDuration(stepElapsedMs(s))}</span>
                              ) : (
                                <input type="number" min="0" style={styles.timeEditInput} value={Math.round(stepElapsedMs(s) / 60000)} onChange={(e) => setStepMinutes(o.id, "steps", i, Number(e.target.value))} />
                              )}
                              {state === "ej" && <button style={styles.actionBtn} onClick={() => startStep(o.id, "steps", i)}>Starta</button>}
                              {state === "pagar" && <button style={styles.actionBtnDone} onClick={() => completeStep(o.id, "steps", i)}>Klar</button>}
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
                            {canManageStaff ? (
                              staff.map((st) => (
                                <button key={st.id} type="button" style={styles.staffToggle(s.staffIds.includes(st.id))} onClick={() => toggleStepStaff(o.id, "steps", i, st.id)}>
                                  {st.name.split(" ")[0]}
                                </button>
                              ))
                            ) : (
                              <span style={styles.stepStaffText}>{s.staffIds.length ? s.staffIds.map((id) => staffName(id).split(" ")[0]).join(", ") : "Otilldelad"}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    <div style={styles.extraLabel}>Tillval</div>
                    {o.extraSteps.map((s, i) => {
                      const state = s.done ? "klar" : s.timerRunning ? "pagar" : "ej";
                      return (
                        <div key={i} style={{ marginBottom: 10 }}>
                          <div style={styles.stepRow}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <span style={{ fontSize: 13 }}>{s.name}</span>
                              <span style={styles.stepStatus(state)}>{state === "klar" ? "Klar" : state === "pagar" ? "Pågår" : "Ej påbörjad"}</span>
                            </div>
                            <div style={styles.timerBox}>
                              {state === "pagar" || !canManageWork ? (
                                <span style={styles.timerText}>{state === "ej" ? "--:--" : formatDuration(stepElapsedMs(s))}</span>
                              ) : (
                                <input type="number" min="0" style={styles.timeEditInput} value={Math.round(stepElapsedMs(s) / 60000)} onChange={(e) => setStepMinutes(o.id, "extraSteps", i, Number(e.target.value))} />
                              )}
                              {state === "ej" && <button style={styles.actionBtn} onClick={() => startStep(o.id, "extraSteps", i)}>Starta</button>}
                              {state === "pagar" && <button style={styles.actionBtnDone} onClick={() => completeStep(o.id, "extraSteps", i)}>Klar</button>}
                              {canManageWork && state === "ej" && <button style={styles.btnSmall} onClick={() => removeExtra(o.id, i)}><Trash2 size={12} /></button>}
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
                            {canManageStaff ? (
                              staff.map((st) => (
                                <button key={st.id} type="button" style={styles.staffToggle(s.staffIds.includes(st.id))} onClick={() => toggleStepStaff(o.id, "extraSteps", i, st.id)}>
                                  {st.name.split(" ")[0]}
                                </button>
                              ))
                            ) : (
                              <span style={styles.stepStaffText}>{s.staffIds.length ? s.staffIds.map((id) => staffName(id).split(" ")[0]).join(", ") : "Otilldelad"}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {o.extraSteps.length === 0 && <p style={{ ...styles.sub, margin: "0 0 10px" }}>Inga tillval aktiverade.</p>}

                    {canManageWork && (
                      <div style={{ marginTop: 4 }}>
                        {extraCatalog.filter((name) => !o.extraSteps.some((es) => es.name === name)).map((name) => (
                          <button key={name} style={styles.addExtraBtn} onClick={() => activateExtra(o.id, name)}>+ {name}</button>
                        ))}
                        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                          <input
                            style={{ ...styles.input, flex: 1 }}
                            placeholder="Eget tillval, t.ex. Vaxning"
                            value={customExtra[o.id] || ""}
                            onChange={(e) => setCustomExtra({ ...customExtra, [o.id]: e.target.value })}
                          />
                          <button
                            style={styles.btnGhost}
                            onClick={() => { activateExtra(o.id, customExtra[o.id] || ""); setCustomExtra({ ...customExtra, [o.id]: "" }); }}
                          >
                            <Plus size={13} />Lägg till
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {todaysOrders.length === 0 && <p style={styles.sub}>Inget att planera idag.</p>}
            </div>
          </>
        )}

        {tab === "rapport" && (
          <>
            <h1 style={styles.h1}>Rapport</h1>
            <p style={styles.sub}>Detaljerade rapporter på personal/bonus, ordrar och bokningar</p>

            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "end", marginBottom: 18 }}>
              <div>
                <label style={styles.label}>Från</label>
                <input type="date" style={styles.input} value={reportFrom} onChange={(e) => setReportFrom(e.target.value)} />
              </div>
              <div>
                <label style={styles.label}>Till</label>
                <input type="date" style={styles.input} value={reportTo} onChange={(e) => setReportTo(e.target.value)} />
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button style={reportView === "personal" ? styles.btn : styles.btnGhost} onClick={() => setReportView("personal")}>Personal & bonus</button>
                <button style={reportView === "ordrar" ? styles.btn : styles.btnGhost} onClick={() => setReportView("ordrar")}>Ordrar</button>
                <button style={reportView === "bokningar" ? styles.btn : styles.btnGhost} onClick={() => setReportView("bokningar")}>Bokningar</button>
                <button style={reportView === "narvaro" ? styles.btn : styles.btnGhost} onClick={() => setReportView("narvaro")}>Närvaro</button>
              </div>
            </div>

            {reportView === "personal" && (
              <>
                <div style={styles.panel}>
                  <div style={styles.panelTitle}>Pris per moment</div>
                  <p style={{ fontSize: 12, color: "#6B7789", margin: "0 0 14px" }}>Sätt en fast kronsumma per moment. Tvätten kan sättas lägre eftersom alla bilar tvättas — polering högre eftersom långt ifrån alla bilar poleras.</p>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "end", marginBottom: 14 }}>
                    {stepLabels.map((n) => (
                      <div key={n}>
                        <label style={styles.label}>{n}</label>
                        <input type="number" min="0" style={{ ...styles.input, width: 64 }} value={prices[n] ?? 0} onChange={(e) => setPrices({ ...prices, [n]: Number(e.target.value) })} />
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                  <button
                    style={styles.btnGhost}
                    onClick={() => downloadCSV(
                      `personal-bonus_${reportFrom}_${reportTo}.csv`,
                      ["Personal", "Datum", "Moment", "Bil", "Kund", "Minuter", "Pris", "Bonus"],
                      bonusEvents.filter((e) => e.staffId).map((e) => [staffName(e.staffId), e.datum, e.moment, e.bil, e.kund, e.minuter, e.price, e.bonus.toFixed(2)])
                    )}
                  ><Download size={14} />Ladda ner CSV</button>
                </div>

                {report.map((s) => (
                  <div key={s.id} style={styles.reportCard}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                      <span style={styles.reportName}>{s.name}</span>
                      <span style={{ fontSize: 15, color: "#FF8A34", fontWeight: 600 }}>{s.bonus.toFixed(2)} kr <span style={{ fontSize: 12, color: "#6B7789", fontWeight: 400 }}>({s.count} moment)</span></span>
                    </div>
                    {s.items.map((it, i) => (
                      <div key={i} style={styles.reportItem}>{it.datum} · {it.moment} · {it.bil} ({it.kund}) · à {it.price} kr · {it.minuter} min · <strong style={{ color: "#1657C8" }}>{it.bonus.toFixed(2)} kr</strong></div>
                    ))}
                    {s.items.length === 0 && <div style={{ ...styles.reportItem, borderTop: "none", paddingTop: 0 }}>Inga avklarade moment i perioden.</div>}
                  </div>
                ))}
                {unassignedBonus > 0 && (
                  <p style={{ fontSize: 12, color: "#FF8A34" }}>{unassignedBonus.toFixed(2)} kr är inte fördelat, eftersom vissa avklarade moment saknar tilldelad personal.</p>
                )}
              </>
            )}

            {reportView === "ordrar" && (
              <>
                <div style={styles.cardRow}>
                  <div style={styles.statCard}><div style={styles.statNum}>{ordersInRange.length}</div><div style={styles.statLabel}>Ordrar i perioden</div></div>
                  <div style={styles.statCard}><div style={styles.statNum}>{orderStatusCounts.vantar}</div><div style={styles.statLabel}>Väntar</div></div>
                  <div style={styles.statCard}><div style={styles.statNum}>{orderStatusCounts.pagar}</div><div style={styles.statLabel}>Pågår</div></div>
                  <div style={styles.statCard}><div style={styles.statNum}>{orderStatusCounts.klar}</div><div style={styles.statLabel}>Klara</div></div>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                  <button
                    style={styles.btnGhost}
                    onClick={() => downloadCSV(
                      `ordrar_${reportFrom}_${reportTo}.csv`,
                      ["Datum", "Tid", "Kund", "Bil", "Tjänst", "Personal", "Status", "Antal moment"],
                      ordersInRange.map((o) => [o.datum, o.tid, o.kund, o.bil, o.tjanst, staffName(o.staffId), STATUS[getOrderStatus(o)].label, o.steps.length + o.extraSteps.length])
                    )}
                  ><Download size={14} />Ladda ner CSV</button>
                </div>
                <div style={styles.panel}>
                  {ordersInRange.slice().sort((a, b) => (a.datum + a.tid).localeCompare(b.datum + b.tid)).map((o) => (
                    <div key={o.id} style={styles.row}>
                      <div>
                        <strong>{o.datum} {o.tid}</strong> — {o.kund} · {o.bil}
                        <div style={{ fontSize: 12, color: "#6B7789" }}>{staffName(o.staffId)} · {o.steps.length + o.extraSteps.length} moment</div>
                      </div>
                      <StatusPill status={getOrderStatus(o)} />
                    </div>
                  ))}
                  {ordersInRange.length === 0 && <p style={styles.sub}>Inga ordrar i perioden.</p>}
                </div>
              </>
            )}

            {reportView === "bokningar" && (
              <>
                <div style={styles.cardRow}>
                  <div style={styles.statCard}><div style={styles.statNum}>{bookingsInRange.length}</div><div style={styles.statLabel}>Bokningar i perioden</div></div>
                  <div style={styles.statCard}><div style={styles.statNum}>{bookingStatusCounts.ny}</div><div style={styles.statLabel}>Nya</div></div>
                  <div style={styles.statCard}><div style={styles.statNum}>{bookingStatusCounts.mottagen}</div><div style={styles.statLabel}>Mottagna</div></div>
                  <div style={styles.statCard}><div style={styles.statNum}>{bookingStatusCounts.klar}</div><div style={styles.statLabel}>Klara</div></div>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                  <button
                    style={styles.btnGhost}
                    onClick={() => downloadCSV(
                      `bokningar_${reportFrom}_${reportTo}.csv`,
                      ["Datum", "Företag", "Bil", "Tjänst", "Status", "Hämtning", "Önskad återlämning"],
                      bookingsInRange.map((o) => [
                        o.datum, o.company, o.bil, o.tjanst, BOOKING_STATUS[o.bookingStatus].label,
                        o.hamtning?.mode === "normal" ? "Normal" : `${o.hamtning?.datum || ""} ${o.hamtning?.tid || ""}`,
                        o.aterlamning?.mode === "normal" ? "Normal" : `${o.aterlamning?.datum || ""} ${o.aterlamning?.tid || ""}`,
                      ])
                    )}
                  ><Download size={14} />Ladda ner CSV</button>
                </div>
                <div style={styles.panel}>
                  <div style={styles.panelTitle}>Per företag</div>
                  {Object.entries(bookingsByCompany).map(([name, count]) => (
                    <div key={name} style={styles.row}><span>{name}</span><span>{count} st</span></div>
                  ))}
                  {Object.keys(bookingsByCompany).length === 0 && <p style={styles.sub}>Inga bokningar i perioden.</p>}
                </div>
                <div style={styles.panel}>
                  <div style={styles.panelTitle}>Alla bokningar</div>
                  {bookingsInRange.slice().sort((a, b) => a.datum.localeCompare(b.datum)).map((o) => (
                    <div key={o.id} style={styles.row}>
                      <div>
                        <strong>{o.datum}</strong> — {o.company} · {o.bil}
                        <div style={{ fontSize: 12, color: "#6B7789" }}>{o.tjanst}</div>
                      </div>
                      <BookingStatusPill status={o.bookingStatus} />
                    </div>
                  ))}
                  {bookingsInRange.length === 0 && <p style={styles.sub}>Inga bokningar i perioden.</p>}
                </div>
              </>
            )}

            {reportView === "narvaro" && (
              <>
                {canManageStaff && (
                  <div style={styles.panel}>
                    <div style={styles.panelTitle}>Registrera frånvaro</div>
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "end", marginBottom: 14 }}>
                      <div>
                        <label style={styles.label}>Personal</label>
                        <select style={styles.input} value={newAbsence.staffId} onChange={(e) => setNewAbsence({ ...newAbsence, staffId: e.target.value })}>
                          {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={styles.label}>Typ</label>
                        <select style={styles.input} value={newAbsence.type} onChange={(e) => setNewAbsence({ ...newAbsence, type: e.target.value })}>
                          {ABSENCE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div><label style={styles.label}>Från</label><input type="date" style={styles.input} value={newAbsence.from} onChange={(e) => setNewAbsence({ ...newAbsence, from: e.target.value })} /></div>
                      <div><label style={styles.label}>Till</label><input type="date" style={styles.input} value={newAbsence.to} onChange={(e) => setNewAbsence({ ...newAbsence, to: e.target.value })} /></div>
                      <button style={styles.btn} onClick={addAbsence}><Plus size={14} />Registrera</button>
                    </div>
                    {absencesInRange.map((a) => (
                      <div key={a.id} style={styles.row}>
                        <span>{staffName(a.staffId)} · {a.type} · {a.from}–{a.to}</span>
                        <button style={styles.btnSmall} onClick={() => removeAbsence(a.id)}><Trash2 size={12} /></button>
                      </div>
                    ))}
                    {absencesInRange.length === 0 && <p style={{ ...styles.sub, margin: 0 }}>Ingen frånvaro registrerad i perioden.</p>}
                  </div>
                )}

                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                  <button
                    style={styles.btnGhost}
                    onClick={() => downloadCSV(
                      `narvaro_${reportFrom}_${reportTo}.csv`,
                      ["Personal", "Arbetade timmar", "Antal pass", ...ABSENCE_TYPES],
                      attendanceReport.map((s) => [s.name, s.hours, s.shifts, ...ABSENCE_TYPES.map((t) => s.byType[t])])
                    )}
                  ><Download size={14} />Ladda ner CSV</button>
                </div>

                {attendanceReport.map((s) => (
                  <div key={s.id} style={styles.reportCard}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                      <span style={styles.reportName}>{s.name}</span>
                      <span style={{ fontSize: 14, color: "#1657C8", fontWeight: 600 }}>{s.hours} h · {s.shifts} pass</span>
                    </div>
                    <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                      {ABSENCE_TYPES.map((t) => (
                        <div key={t} style={{ fontSize: 12, color: s.byType[t] > 0 ? "#475467" : "#94A3B8" }}>{t}: <strong>{s.byType[t]}</strong> dagar</div>
                      ))}
                    </div>
                  </div>
                ))}
              </>
            )}
          </>
        )}

        {tab === "statistik" && (
          <>
            <h1 style={styles.h1}>Statistik</h1>
            <p style={styles.sub}>Detaljerad statistik på ordrar per kund, produktion och fördelning</p>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "end", marginBottom: 18 }}>
              <div>
                <label style={styles.label}>Från</label>
                <input type="date" style={styles.input} value={reportFrom} onChange={(e) => setReportFrom(e.target.value)} />
              </div>
              <div>
                <label style={styles.label}>Till</label>
                <input type="date" style={styles.input} value={reportTo} onChange={(e) => setReportTo(e.target.value)} />
              </div>
            </div>

            {statCustomerView ? (
              <>
                <button style={{ ...styles.btnGhost, marginBottom: 16 }} onClick={() => setStatCustomerView(null)}>← Tillbaka till alla kunder</button>
                <div style={styles.panel}>
                  <div style={styles.panelTitle}>{statCustomerView}</div>
                  <div style={styles.cardRow}>
                    <div style={styles.statCard}><div style={styles.statNum}>{customerOrders.length}</div><div style={styles.statLabel}>Bilar i perioden</div></div>
                    <div style={styles.statCard}><div style={styles.statNum}>{customerMomentsDone}</div><div style={styles.statLabel}>Moment utförda</div></div>
                    <div style={styles.statCard}><div style={styles.statNum}>{customerAvgMoments}</div><div style={styles.statLabel}>Snitt moment/bil</div></div>
                  </div>
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 12, color: "#6B7789", marginBottom: 8 }}>Populäraste momenten</div>
                    {customerMomentPopularity.map(([name, count]) => (
                      <div key={name} style={{ marginBottom: 8 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
                          <span>{name}</span><span>{count} st</span>
                        </div>
                        <div style={{ background: "#EEF2FA", height: 8 }}>
                          <div style={{ width: `${(count / customerMaxMoment) * 100}%`, background: "#1657C8", height: 8 }} />
                        </div>
                      </div>
                    ))}
                    {customerMomentPopularity.length === 0 && <p style={{ ...styles.sub, margin: 0 }}>Inga utförda moment i perioden.</p>}
                  </div>
                </div>

                <div style={styles.panel}>
                  <div style={styles.panelTitle}>Status på ordrarna</div>
                  <div style={{ display: "flex", gap: 14, fontSize: 12, color: "#6B7789" }}>
                    <span><span style={styles.dot("#FFC93C")} />Väntar: {customerStatusCounts.vantar}</span>
                    <span><span style={styles.dot("#FF8A34")} />Pågår: {customerStatusCounts.pagar}</span>
                    <span><span style={styles.dot("#1657C8")} />Klar: {customerStatusCounts.klar}</span>
                  </div>
                </div>

                <div style={styles.panel}>
                  <div style={styles.panelTitle}>Ordrar</div>
                  {customerOrders.slice().sort((a, b) => (a.datum + a.tid).localeCompare(b.datum + b.tid)).map((o) => (
                    <div key={o.id} style={styles.row}>
                      <div>
                        <strong>{o.datum}</strong> — {o.bil} {o.tjanst ? "· " + o.tjanst : ""}
                      </div>
                      <StatusPill status={getOrderStatus(o)} />
                    </div>
                  ))}
                  {customerOrders.length === 0 && <p style={styles.sub}>Inga ordrar i perioden.</p>}
                </div>
              </>
            ) : (
              <>
                <div style={styles.panel}>
                  <div style={styles.panelTitle}>Produktion</div>
                  <div style={styles.cardRow}>
                    <div style={styles.statCard}><div style={styles.statNum}>{ordersInRange.length}</div><div style={styles.statLabel}>Bilar i perioden</div></div>
                    <div style={styles.statCard}><div style={styles.statNum}>{totalMomentsDone}</div><div style={styles.statLabel}>Moment utförda</div></div>
                    <div style={styles.statCard}><div style={styles.statNum}>{avgMomentsPerOrder}</div><div style={styles.statLabel}>Snitt moment/bil</div></div>
                  </div>
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 12, color: "#6B7789", marginBottom: 8 }}>Populäraste momenten</div>
                    {momentPopularity.map(([name, count]) => (
                      <div key={name} style={{ marginBottom: 8 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
                          <span>{name}</span><span>{count} st</span>
                        </div>
                        <div style={{ background: "#EEF2FA", height: 8 }}>
                          <div style={{ width: `${(count / maxMomentCount) * 100}%`, background: "#1657C8", height: 8 }} />
                        </div>
                      </div>
                    ))}
                    {momentPopularity.length === 0 && <p style={{ ...styles.sub, margin: 0 }}>Inga utförda moment i perioden.</p>}
                  </div>
                </div>

                <div style={styles.panel}>
                  <div style={styles.panelTitle}>Fördelning</div>
                  <div style={{ fontSize: 12, marginBottom: 10 }}>Andel bokningar från företag: <strong>{bookingShare}%</strong> ({bookingsInRange.length} av {ordersInRange.length})</div>
                  <div style={{ fontSize: 12, color: "#6B7789", marginBottom: 6 }}>Statusfördelning</div>
                  <div style={{ display: "flex", height: 10, marginBottom: 10, overflow: "hidden" }}>
                    <div style={{ width: `${statusShare.vantar}%`, background: "#FFC93C" }} title={`Väntar ${statusShare.vantar}%`} />
                    <div style={{ width: `${statusShare.pagar}%`, background: "#FF8A34" }} title={`Pågår ${statusShare.pagar}%`} />
                    <div style={{ width: `${statusShare.klar}%`, background: "#1657C8" }} title={`Klar ${statusShare.klar}%`} />
                  </div>
                  <div style={{ display: "flex", gap: 14, fontSize: 12, color: "#6B7789" }}>
                    <span><span style={styles.dot("#FFC93C")} />Väntar {statusShare.vantar}%</span>
                    <span><span style={styles.dot("#FF8A34")} />Pågår {statusShare.pagar}%</span>
                    <span><span style={styles.dot("#1657C8")} />Klar {statusShare.klar}%</span>
                  </div>
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                  <button
                    style={styles.btnGhost}
                    onClick={() => downloadCSV(
                      `kundstatistik_${reportFrom}_${reportTo}.csv`,
                      ["Kund", "Antal ordrar", "Moment utförda", "Vanligaste tjänst"],
                      customerStats.map((c) => [c.kund, c.orders, c.momentsDone, c.topTjanst])
                    )}
                  ><Download size={14} />Ladda ner CSV</button>
                </div>
                <div style={styles.panel}>
                  <div style={styles.panelTitle}>Statistik per kund</div>
                  <p style={{ fontSize: 12, color: "#6B7789", margin: "0 0 10px" }}>Klicka på en kund för att öppna en fokuserad rapport — bra att dela skärm med, utan att andra kunders data syns.</p>
                  {customerStats.map((c) => (
                    <div key={c.kund} style={{ ...styles.row, cursor: "pointer" }} onClick={() => setStatCustomerView(c.kund)}>
                      <div>
                        <strong style={{ color: "#1657C8" }}>{c.kund}</strong>
                        <div style={{ fontSize: 12, color: "#6B7789" }}>Vanligast: {c.topTjanst}</div>
                      </div>
                      <div style={{ textAlign: "right", fontSize: 12, color: "#6B7789" }}>
                        <div><strong style={{ color: "#1657C8" }}>{c.orders}</strong> ordrar</div>
                        <div>{c.momentsDone} moment</div>
                      </div>
                    </div>
                  ))}
                  {customerStats.length === 0 && <p style={styles.sub}>Inga ordrar i perioden.</p>}
                </div>
              </>
            )}
          </>
        )}

        {tab === "loner" && (
          <>
            <h1 style={styles.h1}>Löner</h1>
            <p style={styles.sub}>Beräknat på faktisk stämplad tid (in/ut minus raster) × timlön</p>
            <div style={styles.panel}>
              {payroll.map((p) => (
                <div key={p.id} style={styles.row}>
                  <div><strong>{p.name}</strong><div style={{ fontSize: 12, color: "#6B7789" }}>{p.hours}h · {p.rate} kr/h</div></div>
                  <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 18, fontWeight: 600 }}>{p.pay.toLocaleString("sv-SE")} kr</div>
                </div>
              ))}
              <p style={{ ...styles.sub, marginTop: 12, marginBottom: 0 }}>Stämpla in/ut via Stämpelklocka-fliken för att generera timmar.</p>
            </div>
          </>
        )}

        {tab === "dok" && (
          <>
            <h1 style={styles.h1}>Dokumentation</h1>
            <p style={styles.sub}>Foton och anteckningar per order</p>
            <div style={styles.panel}>
              <label style={styles.label}>Välj order</label>
              <select style={{ ...styles.input, width: "100%", marginBottom: 16 }} value={docOrderId} onChange={(e) => setDocOrderId(e.target.value)}>
                {orders.map((o) => <option key={o.id} value={o.id}>{o.kund} · {o.bil} · {o.datum}</option>)}
              </select>
              {docOrder && (
                <>
                  <label style={styles.label}>Anteckningar</label>
                  <textarea style={{ ...styles.input, width: "100%", minHeight: 70, marginBottom: 16, fontFamily: "inherit" }} value={docOrder.notes} onChange={(e) => updateNote(docOrder.id, e.target.value)} placeholder="Skador, önskemål från kund, avvikelser..." />
                  <label style={styles.label}>Foton</label>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
                    {docOrder.photos.map((p) => <img key={p.id} src={p.src} alt="dokumentation" style={{ width: 96, height: 96, objectFit: "cover", border: "1px solid #D7E0EE" }} />)}
                    <label style={{ ...styles.btnGhost, width: 96, height: 96, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                      <ImagePlus size={18} />
                      <span style={{ fontSize: 11, marginTop: 4 }}>Lägg till</span>
                      <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => e.target.files[0] && addPhoto(docOrder.id, e.target.files[0])} />
                    </label>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {tab === "installningar" && (
          <>
            <h1 style={styles.h1}>Inställningar</h1>
            <p style={styles.sub}>Anpassa namn, moment och tillval för er verksamhet</p>

            <div style={styles.panel}>
              <div style={styles.panelTitle}>Er verksamhet</div>
              <p style={{ fontSize: 12, color: "#6B7789", margin: "0 0 14px" }}>Systemet heter alltid GG — det här är namnet på just ert bilvårdsföretag, visas under GG i menyn.</p>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <label style={styles.label}>Företagsnamn</label>
                  <input style={styles.input} value={shopName} onChange={(e) => setShopName(e.target.value)} />
                </div>
                <div>
                  <label style={styles.label}>Underrubrik</label>
                  <input style={{ ...styles.input, width: 220 }} value={shopSubtitle} onChange={(e) => setShopSubtitle(e.target.value)} placeholder="t.ex. Ort · Verksamhetstyp" />
                </div>
              </div>
            </div>

            <div style={styles.panel}>
              <div style={styles.panelTitle}>Grundmoment</div>
              <p style={{ fontSize: 12, color: "#6B7789", margin: "0 0 14px" }}>De fem stegen varje bil går igenom. De två sista är alltid med och syns aldrig som val för kunden.</p>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                {stepLabels.map((n, i) => (
                  <div key={i}>
                    <label style={styles.label}>{i < 3 ? `Steg ${i + 1} (kundval)` : `Steg ${i + 1} (alltid med, dolt för kund)`}</label>
                    <input style={styles.input} value={n} onChange={(e) => renameStepLabel(i, e.target.value)} />
                  </div>
                ))}
              </div>
            </div>

            <div style={styles.panel}>
              <div style={styles.panelTitle}>Tillvalskatalog</div>
              <p style={{ fontSize: 12, color: "#6B7789", margin: "0 0 14px" }}>Extra tjänster som går att aktivera på en order eller bocka i vid bokning.</p>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                {extraCatalog.map((n) => (
                  <span key={n} style={styles.stationChip}>
                    {n}
                    <button style={styles.stationChipX} onClick={() => removeExtraCatalogItem(n)}>×</button>
                  </span>
                ))}
                {extraCatalog.length === 0 && <span style={{ fontSize: 12, color: "#6B7789" }}>Inga tillval inlagda.</span>}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <input style={{ ...styles.input, flex: 1 }} value={newExtraCatalogItem} onChange={(e) => setNewExtraCatalogItem(e.target.value)} placeholder="t.ex. Fälgrengöring" />
                <button style={styles.btn} onClick={addExtraCatalogItem}><Plus size={14} />Lägg till</button>
              </div>
            </div>
          </>
        )}

        {tab === "konton" && (
          <>
            <h1 style={styles.h1}>Konton</h1>
            <p style={styles.sub}>Hantera alla konton och behörigheter — bara ägaren ser den här fliken</p>

            <div style={styles.panel}>
              <div style={styles.panelTitle}>Personal</div>
              {staff.map((s) => (
                <div key={s.id} style={{ padding: "10px 0", borderBottom: "1px solid #E9EEF7" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                    <div>
                      <strong>{s.name}</strong>
                      <div style={{ fontSize: 12, color: "#6B7789" }}>{s.roll} · {s.status === "inactive" ? <span style={{ color: "#C1560A" }}>Inaktiverat konto</span> : "Aktivt konto"}</div>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <select style={styles.input} value={s.permissionRole || "tekniker"} onChange={(e) => setStaffPermissionRole(s.id, e.target.value)}>
                        <option value="tekniker">Personal</option>
                        <option value="arbetsledare">Arbetsledare</option>
                        <option value="platschef">Platschef</option>
                        <option value="agare">Admin</option>
                      </select>
                      {role === "agare" && s.harKonto && (
                        <button style={styles.btnGhost} onClick={() => aterstallLosenord(s.id, s.name)}>Återställ lösenord</button>
                      )}
                      <button style={styles.btnGhost} onClick={() => toggleStaffStatus(s.id)}>{s.status === "inactive" ? "Aktivera" : "Inaktivera"}</button>
                      <button style={styles.btnSmall} onClick={() => removeStaffAccount(s.id)}><Trash2 size={12} /></button>
                    </div>
                  </div>
                </div>
              ))}
              {staff.length === 0 && <p style={styles.sub}>Ingen personal inlagd.</p>}
            </div>

            <div style={styles.panel}>
              <div style={styles.panelTitle}>Väntar på godkännande</div>
              {registeredCustomers.filter((c) => c.status === "pending").map((c) => (
                <div key={c.id} style={{ padding: "10px 0", borderBottom: "1px solid #E9EEF7" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                    <div>
                      <strong>{c.company}</strong>
                      <div style={{ fontSize: 12, color: "#6B7789" }}>{c.contact ? c.contact + " · " : ""}{c.email}</div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button style={styles.btn} onClick={() => approveCustomerAccount(c.id)}>Godkänn</button>
                      <button style={styles.btnGhost} onClick={() => rejectCustomerAccount(c.id)}>Avslå</button>
                    </div>
                  </div>
                </div>
              ))}
              {registeredCustomers.filter((c) => c.status === "pending").length === 0 && <p style={styles.sub}>Inga väntande registreringar.</p>}
            </div>

            <div style={styles.panel}>
              <div style={styles.panelTitle}>Företagskunder</div>
              {registeredCustomers.filter((c) => c.status !== "pending").map((c) => (
                <div key={c.id} style={{ padding: "10px 0", borderBottom: "1px solid #E9EEF7" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                    <div>
                      <strong>{c.company}</strong>
                      <div style={{ fontSize: 12, color: "#6B7789" }}>{c.contact ? c.contact + " · " : ""}{c.email} · {c.status === "inactive" ? <span style={{ color: "#C1560A" }}>Inaktiverat konto</span> : "Aktivt konto"}</div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button style={styles.btnGhost} onClick={() => toggleCustomerStatus(c.id)}>{c.status === "inactive" ? "Aktivera" : "Inaktivera"}</button>
                      <button style={styles.btnSmall} onClick={() => removeCustomerAccount(c.id)}><Trash2 size={12} /></button>
                    </div>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 11, color: "#6B7789", marginBottom: 6 }}>Kontaktpersoner (kan logga in med egen e-post)</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                      {(c.contacts || []).map((ct) => (
                        <span key={ct.id} style={styles.stationChip}>
                          {ct.name}{ct.email ? " · " + ct.email : ""}
                          <button style={styles.stationChipX} onClick={() => adminRemoveContact(c.id, ct.id)}>×</button>
                        </span>
                      ))}
                      {(!c.contacts || c.contacts.length === 0) && <span style={{ fontSize: 12, color: "#6B7789" }}>Inga kontaktpersoner tillagda.</span>}
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <input style={{ ...styles.input, width: 160 }} value={getAdminContactForm(c.id).name} onChange={(e) => setAdminContactForm({ ...adminContactForm, [c.id]: { ...getAdminContactForm(c.id), name: e.target.value } })} placeholder="Namn" />
                      <input style={{ ...styles.input, width: 200 }} value={getAdminContactForm(c.id).email} onChange={(e) => setAdminContactForm({ ...adminContactForm, [c.id]: { ...getAdminContactForm(c.id), email: e.target.value } })} placeholder="E-post" />
                      <input type="password" style={{ ...styles.input, width: 170 }} value={getAdminContactForm(c.id).password ?? ""} onChange={(e) => setAdminContactForm({ ...adminContactForm, [c.id]: { ...getAdminContactForm(c.id), password: e.target.value } })} placeholder="Lösenord (valfritt)" />
                      <button style={styles.btnGhost} onClick={() => adminAddContact(c.id)}><Plus size={13} />Lägg till kontaktperson</button>
                      {/* Företagsnamnet kommer från raden vi står på, så det
                          kan inte bli felstavat och hamna på fel företag. */}
                      <button
                        style={styles.btn}
                        onClick={() => bjudInKontakt(c)}
                        disabled={!getAdminContactForm(c.id).name.trim() || !getAdminContactForm(c.id).email.trim()}
                      >Bjud in</button>
                    </div>
                    <p style={{ fontSize: 11, color: "#94A3B8", margin: "6px 0 0", lineHeight: 1.5 }}>
                      <strong>Bjud in</strong> skickar en länk där personen väljer sitt eget lösenord — kräver namn och e-post.<br />
                      <strong>Lägg till</strong> skapar posten direkt: med lösenord får personen ett konto, utan blir det bara en anteckning i registret.
                    </p>
                  </div>
                </div>
              ))}
              {registeredCustomers.filter((c) => c.status !== "pending").length === 0 && <p style={styles.sub}>Inga registrerade företagskunder.</p>}
            </div>

            <div style={styles.panel}>
              <div style={styles.panelTitle}>Väntande inbjudningar</div>
              {pendingInvites.map((inv) => (
                <div key={inv.id} style={styles.row}>
                  <span>{inv.name} · {inv.email} · {inv.role === "tekniker" ? "Personal" : inv.role === "arbetsledare" ? "Arbetsledare" : inv.role === "platschef" ? "Platschef" : "Admin"}</span>
                  <button style={styles.btnSmall} onClick={() => cancelInvite(inv.id)}><Trash2 size={12} /></button>
                </div>
              ))}
              {pendingCustomerInvites.map((inv) => (
                <div key={inv.id} style={styles.row}>
                  <span>{inv.company} · {inv.email} · Företagskund</span>
                  <button style={styles.btnSmall} onClick={() => cancelCustomerInvite(inv.id)}><Trash2 size={12} /></button>
                </div>
              ))}
              {pendingInvites.length === 0 && pendingCustomerInvites.length === 0 && <p style={styles.sub}>Inga väntande inbjudningar.</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
